"use client";
import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Menu,
  Tooltip,
  MenuItem,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  DashboardCustomize,
  ShowChart,
  MoveToInbox,
  Person as PersonIcon,
  BarChart as BarChartIcon,
  CalendarToday,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../Configurations/Urls";
import usePermissionDataStore from "../../Zustand/Store/usePermissionDataStore";
import { emsRowMatchesLoggedInTrainer } from "../../Utils/emsTrainerUserMatch";
import * as XLSX from "xlsx";
import {
  UserCog, Boxes, GraduationCap, Target, TrendingUp, TrendingDown,
  Minus, ArrowRight, RefreshCw, Plus, UserPlus, CalendarClock,
  Wallet, AlertCircle, Download, BookOpen,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Cell,
} from "recharts";

export default function Dashboard() {
  const navigate = useNavigate();
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [employees, setEmployees] = React.useState([]);
  const [interns, setInterns] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // Lead summary (replaces other dashboard widgets)
  const [leadsSummary, setLeadsSummary] = React.useState(null);
  const [leadsSummaryLoading, setLeadsSummaryLoading] =
    React.useState(false);
  const [leadsSummaryError, setLeadsSummaryError] = React.useState(null);
  const [feeInstallmentDueCounts, setFeeInstallmentDueCounts] = React.useState({
    monthPending: null,
    todayPending: null,
    overdue: null,
  });

  /** Batch trainer → batch details (dashboard Fees section) */
  const [batchTrainerList, setBatchTrainerList] = React.useState([]);
  const [batchTrainerListLoading, setBatchTrainerListLoading] =
    React.useState(false);
  const [selectedBatchTrainerId, setSelectedBatchTrainerId] =
    React.useState("");
  const [batchDetails, setBatchDetails] = React.useState(null);
  const [batchDetailsLoading, setBatchDetailsLoading] = React.useState(false);
  const [batchDetailsError, setBatchDetailsError] = React.useState(null);
  /** `null` = loading; map `organization_ems_batch_id` → enrolled student count */
  const [batchStudentCountByBatchId, setBatchStudentCountByBatchId] =
    React.useState(null);
  /** 'all' | number — category index from `training_program_graph.categories` */
  const [trainingGraphCategory, setTrainingGraphCategory] =
    React.useState("all");

  const { Permission, setPermission } = usePermissionDataStore();

  const [onLeaveTodayApiCount, setOnLeaveTodayApiCount] = React.useState(null);

  const [apiUrl, setApiUrl] = React.useState(null);
  const [internsApiUrl, setInternsApiUrl] = React.useState(null);

  const [freelancers, setFreelancers] = React.useState([]);
  const [freelancersApiUrl, setFreelancersApiUrl] = React.useState(null);

  React.useEffect(() => {
    if (org?.organization_id) {
      setApiUrl(
        `${MAIN_URL}/api/organizations/${org?.organization_id}/filter-employee`
      );
      setInternsApiUrl(
        `${MAIN_URL}/api/organizations/${org?.organization_id}/filter/intern`
      );
      setFreelancersApiUrl(
        `${MAIN_URL}/api/organizations/${org?.organization_id}/freelance-employees`
      );
    }
  }, [org?.organization_id]);

  // Keep Fee cards consistent with fee-installment list filters.
  React.useEffect(() => {
    const organizationId = org?.organization_id;
    if (!organizationId) {
      setFeeInstallmentDueCounts({
        monthPending: null,
        todayPending: null,
        overdue: null,
      });
      return;
    }

    let isActive = true;
    const loadFeeInstallmentCounts = async () => {
      try {
        const token = localStorage.getItem("token");
        const url = `${MAIN_URL}/api/ems/fee-installments?organization_id=${organizationId}&per_page=all`;
        const res = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          mode: "cors",
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status} for ${url}`);
        }
        const json = await res.json();
        const rows = Array.isArray(json?.data?.data)
          ? json.data.data
          : Array.isArray(json?.data)
            ? json.data
            : [];

        const now = new Date();
        const todayStart = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate()
        );
        const todayEnd = new Date(todayStart);
        todayEnd.setHours(23, 59, 59, 999);
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

        const toDate = (v) => {
          if (!v) return null;
          const s = String(v).trim();
          if (!s) return null;
          const iso = s.length >= 10 ? s.slice(0, 10) : s;
          const d = new Date(iso);
          return Number.isNaN(d.getTime()) ? null : d;
        };
        const statusOf = (r) => String(r?.status ?? "").trim().toLowerCase();

        const monthPending = rows.filter((r) => {
          const d = toDate(r?.due_date);
          const st = statusOf(r);
          return d && d >= monthStart && d <= monthEnd && st === "pending";
        }).length;

        const todayPending = rows.filter((r) => {
          const d = toDate(r?.due_date);
          const st = statusOf(r);
          return d && d >= todayStart && d <= todayEnd && st === "pending";
        }).length;

        const overdue = rows.filter((r) => {
          const d = toDate(r?.due_date);
          const st = statusOf(r);
          return d && d < todayStart && st !== "paid" && st !== "cancelled";
        }).length;

        if (!isActive) return;
        setFeeInstallmentDueCounts({ monthPending, todayPending, overdue });
      } catch (e) {
        if (!isActive) return;
        setFeeInstallmentDueCounts({
          monthPending: null,
          todayPending: null,
          overdue: null,
        });
      }
    };

    loadFeeInstallmentCounts();
    return () => {
      isActive = false;
    };
  }, [org?.organization_id]);

  React.useEffect(() => {
    const organizationId = org?.organization_id;
    if (!organizationId) {
      setBatchTrainerList([]);
      return;
    }
    let isActive = true;
    const load = async () => {
      setBatchTrainerListLoading(true);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${MAIN_URL}/api/ems/batch-classes?organization_id=${organizationId}&per_page=all`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            mode: "cors",
          }
        );
        const json = await res.json();
        const rows = Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json?.data?.data)
            ? json.data.data
            : [];
        if (!isActive) return;
        setBatchTrainerList(rows);
      } catch {
        if (isActive) setBatchTrainerList([]);
      } finally {
        if (isActive) setBatchTrainerListLoading(false);
      }
    };
    load();
    return () => {
      isActive = false;
    };
  }, [org?.organization_id]);

  React.useEffect(() => {
    const organizationId = org?.organization_id;
    if (!organizationId) {
      setBatchStudentCountByBatchId(null);
      return;
    }
    let isActive = true;
    const load = async () => {
      setBatchStudentCountByBatchId(null);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${MAIN_URL}/api/ems/batch-students?organization_id=${organizationId}&per_page=all`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            mode: "cors",
          }
        );
        if (!res.ok) {
          if (isActive) setBatchStudentCountByBatchId({});
          return;
        }
        const json = await res.json();
        const rows = Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json?.data?.data)
            ? json.data.data
            : [];
        const counts = {};
        rows.forEach((r) => {
          const bid =
            r?.organization_ems_batch_id ?? r?.batch?.organization_ems_batch_id;
          if (bid == null || bid === "") return;
          const k = String(bid);
          counts[k] = (counts[k] || 0) + 1;
        });
        if (!isActive) return;
        setBatchStudentCountByBatchId(counts);
      } catch {
        if (isActive) setBatchStudentCountByBatchId({});
      }
    };
    load();
    return () => {
      isActive = false;
    };
  }, [org?.organization_id]);

  React.useEffect(() => {
    const organizationId = org?.organization_id;
    if (!organizationId || !selectedBatchTrainerId) {
      setBatchDetails(null);
      setBatchDetailsError(null);
      return;
    }
    // Previously this dashboard called `/batch-details` using the selected id.
    // That endpoint expects `organization_ems_batch_trainer_id`.
    // Now the dropdown selection is based on `trainer_employee_id`,
    // so we derive batch details directly from `batchTrainerList` instead.
    setBatchDetails(null);
    setBatchDetailsError(null);
    setBatchDetailsLoading(false);
  }, [org?.organization_id, selectedBatchTrainerId]);

  const resolveTrainerName = (item) => {
    const appUserName = String(
      item?.trainer?.applicationuser?.[0]?.full_name ??
        item?.trainer?.application_user?.[0]?.full_name ??
        item?.trainer?.application_user?.full_name ??
        ""
    )
      .replace(/\s+/g, " ")
      .trim();
    if (appUserName) return appUserName;

    const personFullName = String(
      item?.trainer?.organization_person?.person?.full_name ??
        item?.trainer?.organizationPerson?.person?.full_name ??
        item?.trainer?.person?.full_name ??
        ""
    )
      .replace(/\s+/g, " ")
      .trim();
    if (personFullName) return personFullName;

    const fn =
      item?.trainer?.person?.first_name ??
      item?.trainer?.organization_person?.person?.first_name ??
      "";
    const ln =
      item?.trainer?.person?.last_name ??
      item?.trainer?.organization_person?.person?.last_name ??
      "";
    const combined = `${fn} ${ln}`.replace(/\s+/g, " ").trim();
    return combined || "Trainer";
  };

  // Trainer dropdown label: only trainer name (no batch/module).
  const batchTrainerLabel = (item) => resolveTrainerName(item);

  const formatDateDMY = (iso) => {
    if (!iso) return "-";
    const s = String(iso).slice(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return s;
    return `${m[3]}-${m[2]}-${m[1]}`;
  };

  const latestRecordForRow = (row) =>
    row?.latestBatchClassRecord ?? row?.latest_batch_class_record ?? null;

  const classDateForRow = (row) =>
    latestRecordForRow(row)?.class_date ??
    row?.class_date ??
    null;

  const classStartTimeForRow = (row) =>
    row?.start_time ??
    latestRecordForRow(row)?.start_time ??
    row?.batch?.batch_start_time ??
    row?.batch_start_time ??
    "";

  const classEndTimeForRow = (row) =>
    row?.end_time ??
    latestRecordForRow(row)?.end_time ??
    row?.batch?.batch_end_time ??
    row?.batch_end_time ??
    "";

  const classModeForRow = (row) =>
    latestRecordForRow(row)?.mode ?? row?.mode ?? row?.batch?.batch_mode ?? "—";

  const classTopicForRow = (row) =>
    latestRecordForRow(row)?.topic ?? row?.topic ?? "—";

  const classStatusForRow = (row) =>
    latestRecordForRow(row)?.status ?? row?.class_status ?? "—";

  const classLocationForRow = (row) =>
    latestRecordForRow(row)?.training_space?.space_name ??
    row?.training_space?.space_name ??
    row?.TrainingSpace?.space_name ??
    (latestRecordForRow(row)?.ems_training_space_id
      ? `Training Space #${latestRecordForRow(row).ems_training_space_id}`
      : null) ??
    (row?.ems_training_space_id ? `Training Space #${row.ems_training_space_id}` : null) ??
    "—";

  const classLastRecordForRow = (row) => {
    const latestStartRaw = latestRecordForRow(row)?.start_time ?? "";
    const statusPart = classStatusForRow(row);
    if (!latestStartRaw) return statusPart || "—";
    const datePart = String(latestStartRaw).includes(" ")
      ? String(latestStartRaw).split(" ")[0]
      : String(latestStartRaw).slice(0, 10);
    const formattedDate = formatDateDMY(datePart);
    if (!statusPart || statusPart === "—") return formattedDate;
    return `${formattedDate} ${statusPart}`;
  };

  const BATCH_DAY_ORDER = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const parseBatchDaysList = (days) => {
    if (days == null) return [];
    if (Array.isArray(days)) {
      return days.map((d) => String(d).trim()).filter(Boolean);
    }
    if (typeof days === "string") {
      const t = days.trim();
      if (!t) return [];
      if (t.startsWith("[")) {
        try {
          const parsed = JSON.parse(t);
          if (Array.isArray(parsed)) {
            return parsed.map((d) => String(d).trim()).filter(Boolean);
          }
        } catch {
          /* fall through */
        }
      }
      return t.split(",").map((d) => d.trim()).filter(Boolean);
    }
    return [];
  };

  /** Monday → Sunday; unknown labels sort last. All days preserved (deduped by weekday). */
  const batchDaySortIndex = (token) => {
    const s = String(token).trim().toLowerCase();
    for (let i = 0; i < BATCH_DAY_ORDER.length; i++) {
      const w = BATCH_DAY_ORDER[i];
      if (s === w) return i;
    }
    for (let i = 0; i < BATCH_DAY_ORDER.length; i++) {
      const w = BATCH_DAY_ORDER[i];
      if (s.startsWith(w.slice(0, 3))) return i;
    }
    return 999;
  };

  const formatBatchDays = (days) => {
    const list = parseBatchDaysList(days);
    if (!list.length) {
      if (days != null && typeof days === "object" && !Array.isArray(days)) {
        try {
          return JSON.stringify(days);
        } catch {
          return "-";
        }
      }
      return "-";
    }
    const tagged = list.map((raw) => ({
      raw,
      ord: batchDaySortIndex(raw),
    }));
    tagged.sort((a, b) => {
      if (a.ord !== b.ord) return a.ord - b.ord;
      return String(a.raw).localeCompare(String(b.raw));
    });
    const seen = new Set();
    const out = [];
    tagged.forEach(({ raw, ord }) => {
      const key = ord === 999 ? `u:${raw}` : `d:${ord}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push(raw);
    });
    return out.join(", ");
  };

  /** DD-MM-YYYY on one line (hyphens must not trigger breaks from `break-word`). */
  const trainerDetailsDateCellSx = {
    whiteSpace: "nowrap",
    wordBreak: "normal",
    overflowWrap: "normal",
    verticalAlign: "top",
    minWidth: 104,
  };

  /** `h:mm AM` must stay on one line (space before AM/PM must not wrap). */
  const trainerDetailsTimeCellSx = {
    whiteSpace: "nowrap",
    wordBreak: "normal",
    overflowWrap: "normal",
    verticalAlign: "middle",
    minWidth: 96,
  };

  /** One line per cell; table scrolls horizontally instead of wrapping into “next line”. */
  const trainerDetailsSingleLineCellSx = {
    whiteSpace: "nowrap",
    wordBreak: "normal",
    overflowWrap: "normal",
    verticalAlign: "middle",
  };

  const formatTimeAmPm = (value) => {
    if (!value) return "-";
    const s = String(value).trim();
    if (!s) return "-";
    const m24 = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (m24) {
      const hour24 = Number(m24[1]);
      const minutes = m24[2];
      if (Number.isNaN(hour24) || hour24 < 0 || hour24 > 23) return s;
      const ampm = hour24 >= 12 ? "PM" : "AM";
      const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
      return `${hour12}:${minutes}\u00A0${ampm}`;
    }
    const m12 = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
    if (m12) {
      const h = Number(m12[1]);
      const min = m12[2] != null ? m12[2] : "00";
      return `${h}:${min.padStart(2, "0")}\u00A0${m12[3].toUpperCase()}`;
    }
    return s;
  };

  /** For sorting: minutes from midnight. Supports 24h `HH:MM` / `HH:MM:SS` and 12h `h AM/PM`. */
  const minutesSinceMidnight = (value) => {
    if (value == null) return NaN;
    const s = String(value).trim();
    if (!s) return NaN;
    const m24 = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (m24) {
      const h = Number(m24[1]);
      const min = Number(m24[2]);
      if (Number.isNaN(h) || Number.isNaN(min) || h < 0 || h > 23) return NaN;
      return h * 60 + min;
    }
    const m12 = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
    if (m12) {
      let h = Number(m12[1]);
      const min = Number(m12[2] != null ? Number(m12[2]) : 0);
      const mer = m12[3].toUpperCase();
      if (Number.isNaN(h) || Number.isNaN(min)) return NaN;
      if (mer === "AM") {
        if (h === 12) h = 0;
      } else {
        if (h !== 12) h += 12;
      }
      return h * 60 + min;
    }
    return NaN;
  };
  

  const firstSelfTrainerAssignmentRow = React.useMemo(
    () =>
      (batchTrainerList || []).find((row) =>
        emsRowMatchesLoggedInTrainer(row, userData)
      ) ?? null,
    [batchTrainerList, userData]
  );

  const hasLoggedInTrainerAssignments = Boolean(firstSelfTrainerAssignmentRow);

  const effectiveSelectedBatchTrainerId = hasLoggedInTrainerAssignments
    ? String(firstSelfTrainerAssignmentRow?.trainer_employee_id ?? "")
    : String(selectedBatchTrainerId || "");

  // Dropdown should show each trainer (by trainer_employee_id) once.
  const trainerOptions = React.useMemo(() => {
    const map = new Map();
    const source = hasLoggedInTrainerAssignments
      ? (batchTrainerList || []).filter((row) =>
          emsRowMatchesLoggedInTrainer(row, userData)
        )
      : batchTrainerList || [];
    source.forEach((row) => {
      const teid = row?.trainer_employee_id;
      if (teid == null || teid === "") return;
      const key = String(teid);
      if (!map.has(key)) map.set(key, row);
    });
    return Array.from(map.values());
  }, [batchTrainerList, hasLoggedInTrainerAssignments, userData]);

  // No selection: show all batch-trainer rows from the list API.
  // With selection: filter to that trainer_employee_id only.
  // Keep same rows as batch-classes list API; only trainer filter is applied here.
  // Sort: by trainer name (when showing all), then by batch start time (morning → evening).
  const selectedTrainerAssignments = React.useMemo(() => {
    const all = batchTrainerList || [];
    const rows = hasLoggedInTrainerAssignments
      ? all.filter((row) => emsRowMatchesLoggedInTrainer(row, userData))
      : effectiveSelectedBatchTrainerId
        ? all.filter(
            (row) =>
              String(row?.trainer_employee_id ?? "") ===
              String(effectiveSelectedBatchTrainerId)
          )
        : [...all];
    const trainerNameSortKey = (row) =>
      String(resolveTrainerName(row) || "").toLowerCase();

    const startKey = (row) =>
      minutesSinceMidnight(classStartTimeForRow(row));
    return [...rows].sort((a, b) => {
      if (!effectiveSelectedBatchTrainerId) {
        const nameCmp = trainerNameSortKey(a).localeCompare(
          trainerNameSortKey(b)
        );
        if (nameCmp !== 0) return nameCmp;
      }
      const ma = startKey(a);
      const mb = startKey(b);
      const aBad = Number.isNaN(ma);
      const bBad = Number.isNaN(mb);
      if (aBad && bBad) return 0;
      if (aBad) return 1;
      if (bBad) return -1;
      return ma - mb;
    });
  }, [
    batchTrainerList,
    effectiveSelectedBatchTrainerId,
    hasLoggedInTrainerAssignments,
    userData,
  ]);

  /** Students enrolled in the batch (`organization_ems_batch_id`), shared across trainer rows on the same batch. */
  const batchStudentCountForAssignment = (row) => {
    const bid =
      row?.batch?.organization_ems_batch_id ?? row?.organization_ems_batch_id;
    if (bid == null || bid === "") return "—";
    if (batchStudentCountByBatchId === null) return "…";
    return batchStudentCountByBatchId[String(bid)] ?? 0;
  };

  const exportBatchTrainerDetailsToExcel = () => {
    const rows = selectedTrainerAssignments || [];
    if (!rows.length) return;

    const dataForExcel = rows.map((row) => {
      const bid =
        row?.batch?.organization_ems_batch_id ?? row?.organization_ems_batch_id;
      let studentsDisplay = "—";
      if (bid != null && bid !== "") {
        if (batchStudentCountByBatchId === null) studentsDisplay = "…";
        else studentsDisplay = batchStudentCountByBatchId[String(bid)] ?? 0;
      }

      return {
        Batch: row?.batch?.batch_code || row?.batch_code || "—",
        "Training program":
          row?.batch?.training_program?.training_program_name || "—",
        Trainer: batchTrainerLabel(row),
        Students: studentsDisplay,
        "Class date": formatDateDMY(classDateForRow(row)),
        "Start time": formatTimeAmPm(classStartTimeForRow(row)),
        "End time": formatTimeAmPm(classEndTimeForRow(row)),
        "Batch days": formatBatchDays(row?.batch?.batch_days),
        Mode: classModeForRow(row),
        Location: classLocationForRow(row),
        "Last record": classLastRecordForRow(row),
        Topic: classTopicForRow(row),
        Status: classStatusForRow(row),
        "Batch name": row?.batch?.batch_name || row?.batch_name || "—",
        "Start date": formatDateDMY(row?.batch?.start_date),
        "End date": formatDateDMY(row?.batch?.end_date),
      };
    });

    const ws = XLSX.utils.json_to_sheet(dataForExcel);
    const headers = Object.keys(dataForExcel[0]);
    ws["!cols"] = headers.map((h) => {
      const headerLen = h.length;
      const maxData = dataForExcel.reduce((max, row) => {
        const v = row[h];
        const len = v == null ? 0 : String(v).length;
        return Math.max(max, len);
      }, headerLen);
      return { wch: Math.min(Math.max(maxData + 2, headerLen + 1), 55) };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Batch trainer details");

    const stamp = new Date().toISOString().slice(0, 10);
    const suffix = effectiveSelectedBatchTrainerId
      ? `_trainer_${effectiveSelectedBatchTrainerId}`
      : "_all_trainers";
    const filename = `batch_trainer_details${suffix}_${stamp}.xlsx`;

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Fetch lead summary for the dashboard (replaces other widgets)
  React.useEffect(() => {
    const organizationId = org?.organization_id;
    if (!organizationId) {
      setLeadsSummary(null);
      setLeadsSummaryLoading(false);
      if (org) {
        setLeadsSummaryError("organization_id not found for this organization.");
      }
      return;
    }

    let isActive = true;
    const load = async () => {
      try {
        setLeadsSummaryLoading(true);
        const token = localStorage.getItem("token");
        // Exact route you shared:
        // Route::get('/leads-summary', ...) => https://api.erp.nsplprojects.com/api/ems/leads-summary
        const candidateUrls = [
          `${MAIN_URL}/api/ems/leads-summary?organization_id=${organizationId}`,
        ];

        let lastError = null;
        for (const url of candidateUrls) {
          try {
            const res = await fetch(url, {
              method: "GET",
              headers: {
                Accept: "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              mode: "cors",
            });
            if (!res.ok) {
              throw new Error(`HTTP ${res.status} for ${url}`);
            }
            const json = await res.json();
            if (!isActive) return;
            console.log("json is : ", json);
            setLeadsSummary(json?.data || null);
            setLeadsSummaryError(null);
            lastError = null;
            break;
          } catch (e) {
            lastError = e;
          }
        }

        if (isActive && lastError) {
          setLeadsSummary(null);
          setLeadsSummaryError(
            `Lead summary failed. Tried: ${candidateUrls.join(
              " | "
            )}. Last error: ${String(lastError?.message || lastError)}`
          );
        }
      } catch (e) {
        if (!isActive) return;
        setLeadsSummary(null);
        setLeadsSummaryError(String(e?.message || e));
      } finally {
        if (!isActive) return;
        setLeadsSummaryLoading(false);
      }
    };

    load();
    return () => {
      isActive = false;
    };
  }, [org?.organization_id]);

  console.log("dsata is ojoj", internsApiUrl);
  

  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const theme = useTheme();

  React.useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme.palette.mode);
  }, [theme.palette.mode]);

  const currentDate = new Date();
  const options = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };
  const formattedDate = currentDate.toLocaleString("en-US", options);
  const currentMonthName = currentDate.toLocaleString("en-US", { month: "long" });

  const TRAINING_CATEGORY_COLORS = React.useMemo(
    () => [
      "#1565c0",
      "#2e7d32",
      "#ef6c00",
      "#c62828",
      "#6a1b9a",
      "#00695c",
      "#0277bd",
      "#5d4037",
      "#455a64",
    ],
    []
  );

  const CHART_PLOT_HEIGHT = 300;

  const trainingChartData = React.useMemo(() => {
    const tpGraph = leadsSummary?.training_program_graph;
    const tpCategories = Array.isArray(tpGraph?.categories)
      ? tpGraph.categories
      : [];
    const tpPrograms = Array.isArray(tpGraph?.programs) ? tpGraph.programs : [];
    const tpValues = Array.isArray(tpGraph?.values) ? tpGraph.values : [];

    if (!tpPrograms.length || !tpValues.length) {
      return { bars: [], maxVal: 1, hasMatrix: false };
    }

    const numCats = tpCategories.length;

    if (trainingGraphCategory === "all") {
      const allBars = tpPrograms.map((programName, j) => {
        const segments = [];
        let total = 0;
        for (let i = 0; i < numCats; i++) {
          const v = Number(tpValues[i]?.[j] ?? 0);
          if (v > 0) {
            segments.push({
              categoryIndex: i,
              label: tpCategories[i] ?? `Category ${i}`,
              value: v,
              color:
                TRAINING_CATEGORY_COLORS[i % TRAINING_CATEGORY_COLORS.length],
            });
          }
          total += v;
        }
        return { programName, programIndex: j, segments, total };
      });
      const bars = allBars.filter((b) => b.total > 0);
      const maxVal = Math.max(
        1,
        ...bars.map((b) => b.total),
        1
      );
      return { bars, maxVal, hasMatrix: true };
    }

    const ci = trainingGraphCategory;
    if (typeof ci !== "number" || ci < 0 || ci >= numCats) {
      return { bars: [], maxVal: 1, hasMatrix: true };
    }

    const row = tpValues[ci] || [];
    const bars = tpPrograms
      .map((programName, j) => {
        const v = Number(row[j] ?? 0);
        return {
          programName,
          programIndex: j,
          segments:
            v > 0
              ? [
                  {
                    categoryIndex: ci,
                    label: tpCategories[ci],
                    value: v,
                    color:
                      TRAINING_CATEGORY_COLORS[
                        ci % TRAINING_CATEGORY_COLORS.length
                      ],
                  },
                ]
              : [],
          total: v,
        };
      })
      .filter((b) => b.total > 0);

    const maxVal = Math.max(1, ...bars.map((b) => b.total));
    return { bars, maxVal, hasMatrix: true };
  }, [leadsSummary, trainingGraphCategory, TRAINING_CATEGORY_COLORS]);

  const calculateWidgetValues = React.useMemo(() => {
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    let newJoiningCount = 0;
    let birthdayCount = 0;
    let anniversaryCount = 0;

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    employees.forEach((employee) => {
      // Process Date of Joining
      if (employee.Date_of_Joining) {
        const [dayStr, monthStr, yearStr] = employee.Date_of_Joining.split("-");
        const joinMonth = monthNames.indexOf(monthStr);
        const joinYear = Number.parseInt(yearStr);

        if (joinMonth === currentMonth) {
          if (joinYear === currentYear) {
            newJoiningCount++;
          } else if (joinYear < currentYear) {
            anniversaryCount++;
          }
        }
      }

      // Process Date of Birth (handles ISO format and "dd-MMM-yyyy" format)
      if (employee.Date_of_Birth) {
        let birthDateObj;
        if (employee.Date_of_Birth.includes("T")) {
          // ISO format
          birthDateObj = new Date(employee.Date_of_Birth);
        } else {
          // "dd-MMM-yyyy" format
          const birthParts = employee.Date_of_Birth.split("-");
          if (birthParts.length === 3) {
            const monthIndex = monthNames.indexOf(birthParts[1]);
            if (monthIndex !== -1) {
              birthDateObj = new Date(
                Number(birthParts[2]),
                monthIndex,
                Number(birthParts[0])
              );
            }
          }
        }
        if (birthDateObj && birthDateObj.getMonth() === currentMonth) {
          birthdayCount++;
        }
      }
    });

    return {
      newJoining: newJoiningCount,
      birthday: birthdayCount,
      anniversary: anniversaryCount,
    };
  }, [employees, currentDate]);

  // NEW: Calculate intern widget values
  const calculateInternWidgetValues = React.useMemo(() => {
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    let newJoiningCount = 0;
    let birthdayCount = 0;
    let anniversaryCount = 0;

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    interns.forEach((intern) => {
      // Process Date of Joining
      if (intern.Date_of_Joining) {
        const [dayStr, monthStr, yearStr] = intern.Date_of_Joining.split("-");
        const joinMonth = monthNames.indexOf(monthStr);
        const joinYear = Number.parseInt(yearStr);

        if (joinMonth === currentMonth) {
          if (joinYear === currentYear) {
            newJoiningCount++;
          } else if (joinYear < currentYear) {
            anniversaryCount++;
          }
        }
      }

      // Process Date of Birth (handles ISO format and "dd-MMM-yyyy" format)
      if (intern.Date_of_Birth) {
        let birthDateObj;
        if (intern.Date_of_Birth.includes("T")) {
          // ISO format
          birthDateObj = new Date(intern.Date_of_Birth);
        } else {
          // "dd-MMM-yyyy" format
          const birthParts = intern.Date_of_Birth.split("-");
          if (birthParts.length === 3) {
            const monthIndex = monthNames.indexOf(birthParts[1]);
            if (monthIndex !== -1) {
              birthDateObj = new Date(
                Number(birthParts[2]),
                monthIndex,
                Number(birthParts[0])
              );
            }
          }
        }
        if (birthDateObj && birthDateObj.getMonth() === currentMonth) {
          birthdayCount++;
        }
      }
    });

    return {
      newJoining: newJoiningCount,
      birthday: birthdayCount,
      anniversary: anniversaryCount,
    };
  }, [interns, currentDate]);

  // Calculate freelancer widget values
  const calculateFreelancerWidgetValues = React.useMemo(() => {
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    let newJoiningCount = 0;
    let birthdayCount = 0;
    let anniversaryCount = 0;

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    freelancers.forEach((freelancer) => {
      if (freelancer.Date_of_Joining) {
        const [dayStr, monthStr, yearStr] =
          freelancer.Date_of_Joining.split("-");
        const joinMonth = monthNames.indexOf(monthStr);
        const joinYear = Number.parseInt(yearStr);

        if (joinMonth === currentMonth) {
          if (joinYear === currentYear) {
            newJoiningCount++;
          } else if (joinYear < currentYear) {
            anniversaryCount++;
          }
        }
      }

      if (freelancer.Date_of_Birth) {
        let birthDateObj;
        if (freelancer.Date_of_Birth.includes("T")) {
          birthDateObj = new Date(freelancer.Date_of_Birth);
        } else {
          const birthParts = freelancer.Date_of_Birth.split("-");
          if (birthParts.length === 3) {
            const monthIndex = monthNames.indexOf(birthParts[1]);
            if (monthIndex !== -1) {
              birthDateObj = new Date(
                Number(birthParts[2]),
                monthIndex,
                Number(birthParts[0])
              );
            }
          }
        }
        if (birthDateObj && birthDateObj.getMonth() === currentMonth) {
          birthdayCount++;
        }
      }
    });

    return {
      newJoining: newJoiningCount,
      birthday: birthdayCount,
      anniversary: anniversaryCount,
    };
  }, [freelancers, currentDate]);

  const attendanceStats = React.useMemo(() => {
    let present = 0;
    let absent = 0;
    let late = 0;

    const normalizeBool = (val) => {
      if (typeof val === "boolean") return val;
      if (typeof val === "number") return val > 0;
      if (typeof val === "string") {
        const s = val.trim().toLowerCase();
        if (
          [
            "true",
            "1",
            "yes",
            "y",
            "present",
            "checked-in",
            "clocked-in",
            "in",
          ].includes(s)
        )
          return true;
        if (
          ["false", "0", "no", "n", "absent", "not-present", "out"].includes(s)
        )
          return false;
      }
      return null;
    };

    const isNonEmptyTime = (val) => {
      if (val == null) return false;
      if (typeof val === "number") return val > 0;
      if (typeof val === "string") {
        const s = val.trim().toLowerCase();
        if (!s) return false;
        if (["null", "undefined", "na", "n/a"].includes(s)) return false;
        if (
          ["00:00", "00:00:00", "0", "1970-01-01", "invalid date"].includes(s)
        )
          return false;
        return true;
      }
      if (val instanceof Date && !isNaN(val.getTime())) return true;
      return false;
    };

    employees.forEach((emp) => {
      const today =
        emp?.attendance_today ||
        emp?.Attendance?.[0] ||
        emp?.attendance?.[0] ||
        null;

      const clockInCandidates = [
        today?.clock_in,
        today?.ClockIn,
        today?.clockIn,
        today?.clockin,
        emp?.clock_in,
        emp?.ClockIn,
        emp?.clockIn,
        emp?.clockin,
      ];
      const clockOutCandidates = [
        today?.clock_out,
        today?.ClockOut,
        today?.clockOut,
        today?.clockout,
        emp?.clock_out,
        emp?.ClockOut,
        emp?.clockOut,
        emp?.clockout,
      ];

      let clockInFlag = null;
      for (const c of clockInCandidates) {
        const n = normalizeBool(c);
        if (n !== null) {
          clockInFlag = n;
          break;
        }
      }

      let clockOutFlag = null;
      for (const c of clockOutCandidates) {
        const n = normalizeBool(c);
        if (n !== null) {
          clockOutFlag = n;
          break;
        }
      }

      const hasCheckInTimestamp = [
        today?.clock_in,
        today?.ClockIn,
        today?.clockIn,
        today?.clockin,
        today?.check_in,
        today?.CheckIn,
        today?.checkIn,
        today?.time_in,
        today?.TimeIn,
        today?.in_time,
        today?.first_check_in,
        today?.firstCheckIn,
      ].some(isNonEmptyTime);

      let counted = false;

      if (clockInFlag === false || clockOutFlag === true) {
        present += 1;
        counted = true;
      } else if (clockInFlag === true && !hasCheckInTimestamp) {
        absent += 1;
        counted = true;
      } else if (hasCheckInTimestamp) {
        present += 1;
        counted = true;
      }

      if (!counted) {
        const rawStatus =
          today?.status ||
          today?.Status ||
          emp?.AttendanceStatus ||
          emp?.today_status ||
          emp?.status_today ||
          "";
        const status =
          typeof rawStatus === "string" ? rawStatus.toLowerCase() : "";
        if (
          status.includes("present") ||
          status === "in" ||
          status === "checked-in"
        )
          present += 1;
        else if (
          status.includes("absent") ||
          status === "out" ||
          status === "checked-out"
        )
          absent += 1;
      }

      if (
        normalizeBool(today?.late) === true ||
        normalizeBool(emp?.late_today) === true
      ) {
        late += 1;
      } else {
        const statusLate = String(
          today?.status || today?.Status || ""
        ).toLowerCase();
        if (statusLate.includes("late")) late += 1;
      }
    });

    return { present, absent, late };
  }, [employees, currentDate]);

  // React.useEffect(() => {
  //   const orgId = org?.organization_id || 5;
  //   if (!orgId) return;

  //   const leavesUrl = `${MAIN_URL}/api/organizations/${orgId}/employee-leaves-all`;
  //   const controller = new AbortController();

  //   const fetchLeaves = async () => {
  //     try {
  //       const response = await fetch(leavesUrl, {
  //         method: "GET",
  //         headers: {
  //           Accept: "application/json",
  //         },
  //         signal: controller.signal,
  //       });
  //       if (!response.ok)
  //         throw new Error(`HTTP error! status: ${response.status}`);

  //       const json = await response.json();
  //       let leaves = Array.isArray(json)
  //         ? json
  //         : json.data || json.leaves || json.results || [];

  //       if (
  //         Array.isArray(leaves) &&
  //         leaves.length > 0 &&
  //         Array.isArray(leaves[0])
  //       ) {
  //         leaves = leaves[0];
  //       }

  //       const y = currentDate.getFullYear();
  //       const m = currentDate.getMonth();
  //       const d = currentDate.getDate();
  //       const isSameDay = (dateObj) =>
  //         dateObj &&
  //         dateObj.getFullYear() === y &&
  //         dateObj.getMonth() === m &&
  //         dateObj.getDate() === d;

  //       const count = (leaves || []).reduce((acc, item) => {
  //         const startStr =
  //           item?.leave_start_date ||
  //           item?.start_date ||
  //           item?.from_date ||
  //           item?.fromDate;
  //         if (!startStr) return acc;
  //         const start = new Date(startStr);
  //         return isSameDay(start) ? acc + 1 : acc;
  //       }, 0);

  //       setOnLeaveTodayApiCount(count);
  //     } catch (e) {
  //       setOnLeaveTodayApiCount(0);
  //     }
  //   };

  //   fetchLeaves();
  //   return () => controller.abort();
  // }, [org?.organization_id, currentDate]);

  const leavesStats = React.useMemo(() => {
    let onLeaveToday = 0;
    let upcomingLeaves = 0;
    let pending = 0;

    const now = currentDate;
    const in7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    employees.forEach((emp) => {
      const todayLeave =
        emp.today_leave ||
        emp.LeaveToday ||
        emp.leave_today ||
        emp.is_on_leave_today ||
        false;
      if (todayLeave) onLeaveToday += 1;

      const pendingReq = Number(
        emp.pending_leave_requests || emp.pendingLeaves || 0
      );
      if (!Number.isNaN(pendingReq)) pending += pendingReq;

      const upcomingArr =
        emp.upcoming_leaves || emp.UpcomingLeaves || emp.upcomingLeaves || [];

      if (Array.isArray(upcomingArr)) {
        upcomingLeaves += upcomingArr.filter((l) => {
          const startStr = l?.start_date || l?.startDate;
          if (!startStr) return false;
          const start = new Date(startStr);
          return start >= now && start <= in7Days;
        }).length;
      }
    });

    const finalOnLeaveToday =
      typeof onLeaveTodayApiCount === "number"
        ? onLeaveTodayApiCount
        : onLeaveToday;

    return { onLeaveToday: finalOnLeaveToday, upcomingLeaves, pending };
  }, [employees, currentDate, onLeaveTodayApiCount]);

  const fetchEmployees = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!apiUrl) {
        setEmployees([]);
        return;
      }

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        mode: "cors",
      });

      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }

      const data = await response.json();
      let employeesData = Array.isArray(data)
        ? data
        : data.employees || data.data || [];

      if (
        Array.isArray(employeesData) &&
        employeesData.length > 0 &&
        Array.isArray(employeesData[0])
      ) {
        employeesData = employeesData[0];
      }

      setEmployees(employeesData);
    } catch (err) {
      let errorMessage = "Failed to fetch employee data";
      if (
        err?.name === "TypeError" &&
        String(err?.message || "").includes("Failed to fetch")
      ) {
        errorMessage =
          "Network error: Unable to connect to the API. This might be due to CORS restrictions or network connectivity issues.";
      } else if (String(err?.message || "").includes("HTTP error")) {
        errorMessage = `API Error: ${err.message}`;
      } else if (err?.message) {
        errorMessage = `Error: ${err.message}`;
      }
      setError(errorMessage);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  // Fetch freelancers function
  const fetchFreelancers = React.useCallback(async () => {
    try {
      if (!freelancersApiUrl) {
        setFreelancers([]);
        return;
      }

      const response = await fetch(freelancersApiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        mode: "cors",
      });

      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }

      const data = await response.json();
      let freelancersData = Array.isArray(data)
        ? data
        : data.freelancers || data.data || [];

      if (
        Array.isArray(freelancersData) &&
        freelancersData.length > 0 &&
        Array.isArray(freelancersData[0])
      ) {
        freelancersData = freelancersData[0];
      }

      setFreelancers(freelancersData);
    } catch (err) {
      console.error("Failed to fetch freelancer data:", err);
      setFreelancers([]);
    }
  }, [freelancersApiUrl]);

  React.useEffect(() => {
    fetchFreelancers();
  }, [fetchFreelancers]);

  // NEW: Fetch interns function
  const fetchInterns = React.useCallback(async () => {
    try {
      if (!internsApiUrl) {
        setInterns([]);
        return;
      }

      const response = await fetch(internsApiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        mode: "cors",
      });

      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      }

      const data = await response.json();
      let internsData = Array.isArray(data)
        ? data
        : data.interns || data.data || [];

      if (
        Array.isArray(internsData) &&
        internsData.length > 0 &&
        Array.isArray(internsData[0])
      ) {
        internsData = internsData[0];
      }

      setInterns(internsData);
    } catch (err) {
      console.error("Failed to fetch intern data:", err);
      setInterns([]);
    }
  }, [internsApiUrl]);



  React.useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  React.useEffect(() => {
    fetchInterns();
  }, [fetchInterns]);

  const handleMenuItemClick = () => {};

  const genderCounts = employees.reduce((acc, emp) => {
    const gender = emp.gender || emp.Gender || emp.sex || emp.Sex || "Unknown";
    acc[gender] = (acc[gender] || 0) + 1;
    return acc;
  }, {});

  const genderData = [
    {
      id: 0,
      value:
        genderCounts["Male"] || genderCounts["male"] || genderCounts["M"] || 0,
      label: "Male",
      color: "#3B82F6",
    },
    {
      id: 1,
      value:
        genderCounts["Female"] ||
        genderCounts["female"] ||
        genderCounts["F"] ||
        0,
      label: "Female",
      color: "#EC4899",
    },
    {
      id: 2,
      value: Object.keys(genderCounts).reduce((otherCount, key) => {
        const normalizedKey = key.toLowerCase();
        if (
          normalizedKey !== "male" &&
          normalizedKey !== "female" &&
          normalizedKey !== "m" &&
          normalizedKey !== "f"
        ) {
          return otherCount + genderCounts[key];
        }
        return otherCount;
      }, 0),
      label: "Other",
      color: "#10B981",
    },
  ];

  const departmentCounts = employees.reduce((acc, emp) => {
    let departmentName = "Unknown";

    if (
      emp.Department &&
      Array.isArray(emp.Department) &&
      emp.Department.length > 0
    ) {
      const deptObj = emp.Department[0];
      departmentName =
        deptObj?.department?.department_short_name ||
        deptObj?.department?.name ||
        deptObj?.department?.Department ||
        deptObj?.department?.department_name ||
        deptObj?.name ||
        deptObj?.Department;
    } else if (emp.department) {
      departmentName =
        emp.department.department_short_name ||
        emp.department.name ||
        emp.department.Department ||
        emp.department.department_name;
    } else if (emp.department_name) {
      departmentName = emp.department_name;
    } else if (typeof emp.Department === "string") {
      departmentName = emp.Department;
    }

    if (!departmentName || departmentName === "") {
      departmentName = "Unknown";
    }

    const getDepartmentAbbreviation = (name) => {
      let cleanName = name.toLowerCase().trim();
      cleanName = cleanName
        .replace(/\s*(department|dept|division|div)\s*$/i, "")
        .trim();

      const abbreviationMap = {
        "human resources": "HR",
        hr: "HR",
        finance: "FIN",
        fin: "FIN",
        "information technology": "IT",
        it: "IT",
        tech: "IT",
        technology: "IT",
        marketing: "MKT",
        mkt: "MKT",
        sales: "SALES",
        operations: "OPS",
        ops: "OPS",
        "research and development": "R&D",
        "r&d": "R&D",
        research: "R&D",
        development: "R&D",
        "customer service": "CS",
        cs: "CS",
        support: "CS",
        legal: "LEGAL",
        administration: "ADMIN",
        admin: "ADMIN",
      };

      return abbreviationMap[cleanName] || name;
    };

    departmentName = getDepartmentAbbreviation(departmentName);

    acc[departmentName] = (acc[departmentName] || 0) + 1;
    return acc;
  }, {});

  const departmentData = Object.entries(departmentCounts).map(
    ([name, value]) => ({ name, value })
  );

  const employmentTypeCounts = employees.reduce((acc, emp) => {
    let employmentTypeName = "Unknown";

    if (emp.employment_type) {
      employmentTypeName = emp.employment_type;
    } else if (
      emp.EmploymentType &&
      Array.isArray(emp.EmploymentType) &&
      emp.EmploymentType.length > 0
    ) {
      const empTypeObj = emp.EmploymentType[0];
      employmentTypeName =
        empTypeObj?.employment_type?.name ||
        empTypeObj?.employment_type?.EmploymentType ||
        empTypeObj?.employment_type?.employment_type_name ||
        empTypeObj?.name ||
        empTypeObj?.EmploymentType;
    } else if (emp.employment_type) {
      employmentTypeName =
        emp.employment_type.name ||
        emp.employment_type.EmploymentType ||
        emp.employment_type.employment_type_name;
    } else if (emp.employment_type_name) {
      employmentTypeName = emp.employment_type_name;
    } else if (typeof emp.EmploymentType === "string") {
      employmentTypeName = emp.EmploymentType;
    }

    if (!employmentTypeName || employmentTypeName === "") {
      employmentTypeName = "Unknown";
    }

    if (employmentTypeName && employmentTypeName !== "Unknown") {
      employmentTypeName = employmentTypeName
        .replace(/\btype\b/gi, "")
        .trim()
        .toUpperCase();
    }

    acc[employmentTypeName] = (acc[employmentTypeName] || 0) + 1;
    return acc;
  }, {});

  const employmentTypeData = Object.entries(employmentTypeCounts).map(
    ([name, value]) => ({ name, value })
  );

  const getShortDesignationName = (designation) => {
    const abbreviationMap = {
      "chief executive officer": "CEO",
      "chief technology officer": "CTO",
      "chief financial officer": "CFO",
      "chief operating officer": "COO",
      "chief marketing officer": "CMO",
      "chief human resources officer": "CHRO",
      "general manager": "GM",
      "project manager": "PM",
      "product manager": "PM",
      "program manager": "PGM",
      "operations manager": "OM",
      "marketing manager": "MM",
      "sales manager": "SM",
      "finance manager": "FinM",
      "hr manager": "HRM",
      "human resources manager": "HRM",
      "security manager": "SM",
      "quality manager": "QM",
      "business development manager": "BDM",
      "software engineer": "SWE",
      "senior software engineer": "SrSE",
      "junior software engineer": "JrSE",
      "software developer": "Dev",
      "senior software developer": "SrDev",
      "junior software developer": "JrDev",
      "full stack developer": "FullStack",
      "frontend developer": "Frontend",
      "backend developer": "Backend",
      "mobile developer": "MobileDev",
      "web developer": "WebDev",
      "devops engineer": "DevOps",
      "system administrator": "SysAdmin",
      "database administrator": "DBA",
      "network administrator": "NetAdmin",
      "quality assurance": "QA",
      "quality assurance engineer": "QA Eng",
      "test engineer": "Test Eng",
      "automation engineer": "Auto Eng",
      "business analyst": "BA",
      "senior business analyst": "Sr BA",
      "data analyst": "DA",
      "senior data analyst": "Sr DA",
      "data scientist": "DS",
      "business intelligence analyst": "BI Analyst",
      "financial analyst": "Fin Analyst",
      "market research analyst": "MR Analyst",
      "sales executive": "Sales Exec",
      "senior sales executive": "Sr Sales",
      "sales representative": "Sales Rep",
      "account manager": "Acct Mgr",
      "account executive": "Acct Exec",
      "marketing executive": "Mkt Exec",
      "digital marketing specialist": "Digital Mkt",
      "content marketing specialist": "Content Mkt",
      "social media manager": "SM Mgr",
      "human resource executive": "HRE",
      "hr executive": "HRE",
      "human resource specialist": "HR Spec",
      "hr specialist": "HR Spec",
      recruiter: "Recruiter",
      "talent acquisition specialist": "TA Spec",
      "administrative assistant": "AdminAsst",
      "executive assistant": "ExecAsst",
      "office manager": "Office Mgr",
      accountant: "Accountant",
      "senior accountant": "Sr Accountant",
      "junior accountant": "Jr Accountant",
      "accounts payable": "AP",
      "accounts receivable": "AR",
      "financial controller": "Controller",
      "finance executive": "FIE",
      "finance head": "FIH",
      "customer service representative": "CSR",
      "customer support": "Support",
      "customer success manager": "CS Mgr",
      "technical support": "Tech Support",
      "help desk": "Help Desk",
      "ui designer": "UI Designer",
      "ux designer": "UX Designer",
      "ui/ux designer": "UI/UX",
      "graphic designer": "GFX Designer",
      "web designer": "Web Designer",
      "product designer": "Product Des",
      "operations executive": "Ops Exec",
      "logistics coordinator": "Logistics",
      "supply chain manager": "SC Mgr",
      "warehouse manager": "WH Mgr",
      "production manager": "Prod Mgr",
      consultant: "Consultant",
      "senior consultant": "Sr Consultant",
      "strategy consultant": "Strategy Con",
      "business consultant": "Biz Consultant",
      "legal counsel": "Legal",
      "compliance officer": "Compliance",
      "legal advisor": "Legal Advisor",
    };

    let cleanDesignation = designation.toLowerCase().trim();

    const suffixesToRemove = [
      " specialist",
      " executive",
      " representative",
      " coordinator",
      " associate",
    ];
    suffixesToRemove.forEach((suffix) => {
      if (
        cleanDesignation.endsWith(suffix) &&
        !abbreviationMap[cleanDesignation]
      ) {
        const withoutSuffix = cleanDesignation.replace(suffix, "").trim();
        if (abbreviationMap[withoutSuffix]) {
          cleanDesignation = withoutSuffix;
        }
      }
    });

    if (abbreviationMap[cleanDesignation]) {
      return abbreviationMap[cleanDesignation];
    }

    for (const [fullName, abbrev] of Object.entries(abbreviationMap)) {
      if (
        fullName.includes(cleanDesignation) ||
        cleanDesignation.includes(fullName.split(" ")[0])
      ) {
        return abbrev;
      }
    }

    const formatted = designation
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    return formatted.length > 12
      ? formatted.substring(0, 12) + "..."
      : formatted;
  };

  const designationCounts = employees.reduce((acc, emp) => {
    const designationName = emp.Designation || "Unknown";
    const shortName = getShortDesignationName(designationName);
    acc[shortName] = (acc[shortName] || 0) + 1;
    return acc;
  }, {});

  const designationData = Object.entries(designationCounts).map(
    ([name, value]) => ({ name, value })
  );

  const ageGroupCounts = employees.reduce((acc, emp) => {
    const age = emp.Age;
    if (age >= 20 && age <= 25) acc["20-25"] = (acc["20-25"] || 0) + 1;
    else if (age >= 26 && age <= 30) acc["26-30"] = (acc["26-30"] || 0) + 1;
    else if (age >= 31 && age <= 35) acc["31-35"] = (acc["31-35"] || 0) + 1;
    else if (age >= 36 && age <= 40) acc["36-40"] = (acc["36-40"] || 0) + 1;
    else if (age > 40) acc["40+"] = (acc["40+"] || 0) + 1;
    return acc;
  }, {});

  const ageGroupData = [
    {
      id: "age20-25",
      value: ageGroupCounts["20-25"] || 0,
      label: "20-25",
      color: "#EF4444",
    },
    {
      id: "age26-30",
      value: ageGroupCounts["26-30"] || 0,
      label: "26-30",
      color: "#F59E0B",
    },
    {
      id: "age31-35",
      value: ageGroupCounts["31-35"] || 0,
      label: "31-35",
      color: "#10B981",
    },
    {
      id: "age36-40",
      value: ageGroupCounts["36-40"] || 0,
      label: "36-40",
      color: "#3B82F6",
    },
    {
      id: "age40+",
      value: ageGroupCounts["40+"] || 0,
      label: "40+",
      color: "#8B5CF6",
    },
  ].filter((item) => item.value > 0);

  const experienceCounts = employees.reduce((acc, emp) => {
    if (emp.Date_of_Joining) {
      try {
        const joiningDate = new Date(emp.Date_of_Joining);
        if (!isNaN(joiningDate.getTime())) {
          const yearsOfExperience =
            currentDate.getFullYear() - joiningDate.getFullYear();

          if (yearsOfExperience >= 0 && yearsOfExperience <= 2)
            acc["0-2 years"] = (acc["0-2 years"] || 0) + 1;
          else if (yearsOfExperience >= 3 && yearsOfExperience <= 5)
            acc["3-5 years"] = (acc["3-5 years"] || 0) + 1;
          else if (yearsOfExperience >= 6 && yearsOfExperience <= 10)
            acc["6-10 years"] = (acc["6-10 years"] || 0) + 1;
          else if (yearsOfExperience > 10)
            acc["10+ years"] = (acc["10+ years"] || 0) + 1;
        }
      } catch (e) {
        console.error(
          `Error processing Date_of_Joining for employee: ${emp.id || emp.name || "Unknown ID"}.`,
          e
        );
      }
    }
    return acc;
  }, {});

  const experienceData = [
    { name: "0-2 years", value: experienceCounts["0-2 years"] || 0 },
    { name: "3-5 years", value: experienceCounts["3-5 years"] || 0 },
    { name: "6-10 years", value: experienceCounts["6-10 years"] || 0 },
    { name: "10+ years", value: experienceCounts["10+ years"] || 0 },
  ];

  const organizationUnitCounts = employees.reduce((acc, emp) => {
    const orgUnitName =
      emp.department_location?.[0]?.department?.department_short_name ||
      emp.department_location?.[0]?.department?.department_name ||
      emp.department?.department_short_name ||
      emp.department?.department_name ||
      emp.department_name ||
      emp.department ||
      "No Department";

    acc[orgUnitName] = (acc[orgUnitName] || 0) + 1;
    return acc;
  }, {});

  const organizationData = Object.entries(organizationUnitCounts).map(
    ([name, value]) => ({ name, value })
  );

  const allChartConfigs = [
    {
      id: "gender",
      title: "Distribution by Gender",
      icon: null,
      type: "pie",
      data: genderData,
      color: "#3B82F6",
      group: "Demographics",
    },
    {
      id: "ageGroup",
      title: "Distribution by Age Group",
      icon: null,
      type: "pie",
      data: ageGroupData,
      color: "#EF4444",
      group: "Demographics",
    },
    (() => {
      const sortedDepartmentData = departmentData.sort(
        (a, b) => b.value - a.value
      );
      return {
        id: "department",
        title: "Distribution by Department",
        icon: null,
        type: "bar",
        data: sortedDepartmentData,
        xAxisData: sortedDepartmentData.map((d) => d.name),
        seriesData: [
          {
            data: sortedDepartmentData.map((d) => d.value),
            label: "Employees",
            color: "#10B981",
          },
        ],
        color: "#10B981",
        group: "Employment Details",
      };
    })(),
    (() => {
      const sortedEmploymentTypeData = employmentTypeData.sort(
        (a, b) => b.value - a.value
      );
      return {
        id: "employmentType",
        title: "Distribution by Employment Type",
        icon: null,
        type: "bar",
        data: sortedEmploymentTypeData,
        xAxisData: sortedEmploymentTypeData.map((d) => d.name),
        seriesData: [
          {
            data: sortedEmploymentTypeData.map((d) => d.value),
            label: "Employees",
            color: "#F97316",
          },
        ],
        color: "#F97316",
        group: "Employment Details",
      };
    })(),
    (() => {
      const experienceOrder = [
        "0-2 years",
        "3-5 years",
        "6-10 years",
        "10+ years",
      ];
      const sortedExperienceData = experienceData.sort((a, b) => {
        return (
          experienceOrder.indexOf(a.name) - experienceOrder.indexOf(b.name)
        );
      });
      return {
        id: "experience",
        title: "Distribution by Experience Level",
        icon: null,
        type: "bar",
        layout: "vertical",
        data: sortedExperienceData,
        xAxisData: sortedExperienceData.map((d) => d.name),
        yAxisData: sortedExperienceData.map((d) => d.name),
        seriesData: [
          {
            data: sortedExperienceData.map((d) => d.value),
            label: "Employees",
            color: "#10B981",
            valueFormatter: (value) => value.toString(),
          },
        ],
        color: "#10B981",
        group: "Employment Details",
      };
    })(),
    (() => {
      const sortedOrganizationData = organizationData.sort(
        (a, b) => b.value - a.value
      );
      return {
        id: "organization",
        title: "Distribution by Organization Units",
        icon: null,
        type: "bar",
        data: sortedOrganizationData,
        xAxisData: sortedOrganizationData.map((d) => d.name),
        seriesData: [
          {
            data: sortedOrganizationData.map((d) => d.value),
            label: "Employees",
            color: "#3B82F6",
          },
        ],
        color: "#3B82F6",
        group: "Employment Details",
      };
    })(),
    (() => {
      const sortedDesignationData = designationData.sort(
        (a, b) => b.value - a.value
      );
      return {
        id: "designation",
        title: "Distribution by Designation",
        icon: null,
        type: "bar",
        layout: "horizontal",
        data: sortedDesignationData,
        yAxisData: sortedDesignationData.map((d) => d.name),
        seriesData: [
          {
            data: sortedDesignationData.map((d) => d.value),
            label: "Employees",
            color: "#8B5CF6",
            valueFormatter: (value) => value.toString(),
          },
        ],
        color: "#8B5CF6",
        group: "Employment Details",
      };
    })(),
  ];



  // const allWidgetConfigs = [
  //   {
  //     id: "totalworkforce",
  //     title: "Total Workforce",
  //     icon: null,
  //     color: "#3B82F6",
  //     group: "Total Workforce",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc`,
  //     value: employees.length,
  //   },
  //   {
  //     id: "totalEmployees",
  //     title: "Employees",
  //     icon: null,
  //     color: "#3B82F6",
  //     group: "Total Workforce",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=employee`,
  //     value: employees.length - freelancers.length,
  //   },
  //  {
  //     id: "totalFreelancers",
  //     title: "Freelancers",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Total Workforce",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=freelancer`,
  //     value: employees.length   - (employees.length - freelancers.length),
  //   },
  //   {
  //     id: "totalEmployees",
  //     title: "Employees",
  //     icon: null,
  //     color: "#3B82F6",
  //     group: "Employees",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=employee`,
  //     value: employees.length,
  //   },
  //   {
  //     id: "newJoiningThisMonth",
  //     title: "New Joining This Month",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Employees",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_Joining_Month=this_month`,
  //     value: calculateWidgetValues.newJoining,
  //   },

  //   {
  //     id: "totalInterns",
  //     title: "Interns",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Interns",
  //     value: interns.length,
  //   },

  //   {
  //     id: "newJoiningThisMonthInterns",
  //     title: "New Joining This Month",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Interns",
  //     value: calculateInternWidgetValues.newJoining,
  //   },

  //   {
  //     id: "totalFreelancers",
  //     title: "Freelancers",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Freelancers",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=freelancer`,
  //     value: freelancers.length,
  //   },

  //   {
  //     id: "newJoiningThisMonthFreelancers",
  //     title: "New Joining This Month",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Freelancers",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_Joining_Month=this_month`,
  //     value: calculateInternWidgetValues.newJoining,
  //   },

  //   {
  //     id: "birthdayThisMonth",
  //     title: "Birthday This Month",
  //     icon: null,
  //     color: "#EC4899",
  //     group: "Occasions",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_Birth_Month=this_month`,
  //     value: calculateWidgetValues.birthday,
  //   },
  //   {
  //     id: "workAnniversariesThisMonth",
  //     title: "Work Anniversaries This Month",
  //     icon: null,
  //     color: "#F59E0B",
  //     group: "Occasions",
  //     link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_Work_Anniversary=this_month`,
  //     value: calculateWidgetValues.anniversary,
  //   },

  //   {
  //     id: "presentToday",
  //     title: "Present Today",
  //     icon: null,
  //     color: "#10B981",
  //     group: "Time & Attendance",
  //     value: attendanceStats.present,
  //   },
  //   {
  //     id: "absentToday",
  //     title: "Absent Today",
  //     icon: null,
  //     color: "#EF4444",
  //     group: "Time & Attendance",
  //     value: attendanceStats.absent,
  //   },
  //   {
  //     id: "lateCheckInsToday",
  //     title: "Late Check-ins Today",
  //     icon: null,
  //     color: "#F59E0B",
  //     group: "Time & Attendance",
  //     value: attendanceStats.late,
  //   },

  //   {
  //     id: "onLeaveToday",
  //     title: "On Leave Today",
  //     icon: null,
  //     color: "#3B82F6",
  //     group: "Leaves",
  //     value: leavesStats.onLeaveToday,
  //   },
  //   {
  //     id: "upcomingLeaves",
  //     title: "Upcoming Leaves (7d)",
  //     icon: null,
  //     color: "#8B5CF6",
  //     group: "Leaves",
  //     value: leavesStats.upcomingLeaves,
  //   },
  //   {
  //     id: "pendingLeaveRequests",
  //     title: "Pending Leave Requests",
  //     icon: null,
  //     color: "#F97316",
  //     group: "Leaves",
  //     value: leavesStats.pending,
  //   },
  // ];


  const allWidgetConfigs = [
    {
      id: "totalworkforce",
      title: "Total Workforce",
      icon: null,
      color: "#3B82F6",
      group: "Total Workforce",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc`,
      value: employees.length,
    },
    {
      id: "totalEmployees",
      title: "Employees",
      icon: null,
      color: "#3B82F6",
      group: "Total Workforce",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=Employee&filter_employment_type_operator=equals`,
      value: employees.length - freelancers.length,
    },
    {
      id: "totalFreelancers",
      title: "Freelancers",
      icon: null,
      color: "#10B981",
      group: "Total Workforce",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=Freelancer&filter_employment_type_operator=equals`,
      value: employees.length - (employees.length - freelancers.length),
    },
    {
      id: "totalEmployees",
      title: "Employees",
      icon: null,
      color: "#3B82F6",
      group: "Employees",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=Employee&filter_employment_type_operator=equals`,
      value: employees.length,
    },
    {
      id: "newJoiningThisMonth",
      title: "New Joining This Month",
      icon: null,
      color: "#10B981",
      group: "Employees",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_date_of_joining=&filter_date_of_joining_operator=this_month_joining`,
      value: calculateWidgetValues.newJoining,
    },

    {
      id: "totalInterns",
      title: "Interns",
      icon: null,
      color: "#10B981",
      group: "Interns",
      value: interns.length,
    },

    {
      id: "newJoiningThisMonthInterns",
      title: "New Joining This Month",
      icon: null,
      color: "#10B981",
      group: "Interns",
      value: calculateInternWidgetValues.newJoining,
    },

    {
      id: "totalFreelancers",
      title: "Freelancers",
      icon: null,
      color: "#10B981",
      group: "Freelancers",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_employment_type=Freelancer&filter_employment_type_operator=equals`,
      value: freelancers.length,
    },

    {
      id: "newJoiningThisMonthFreelancers",
      title: "New Joining This Month",
      icon: null,
      color: "#10B981",
      group: "Freelancers",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_date_of_joining=&filter_date_of_joining_operator=this_month_joining`,
      value: calculateFreelancerWidgetValues.newJoining,
    },

    {
      id: "birthdayThisMonth",
      title: "Birthday This Month",
      icon: null,
      color: "#EC4899",
      group: "Occasions",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_date_of_birth=&filter_date_of_birth_operator=this_month`,
      value: calculateWidgetValues.birthday,
    },
    {
      id: "workAnniversariesThisMonth",
      title: "Work Anniversaries This Month",
      icon: null,
      color: "#F59E0B",
      group: "Occasions",
      link: `/organization/employee/employee-details?page=1&pageSize=10&sortBy=employee_code&sortOrder=asc&filter_date_of_joining=&filter_date_of_joining_operator=this_month_anniversary`,
      value: calculateWidgetValues.anniversary,
    },

    {
      id: "presentToday",
      title: "Present Today",
      icon: null,
      color: "#10B981",
      group: "Time & Attendance",
      value: attendanceStats.present,
    },
    {
      id: "absentToday",
      title: "Absent Today",
      icon: null,
      color: "#EF4444",
      group: "Time & Attendance",
      value: attendanceStats.absent,
    },
    {
      id: "lateCheckInsToday",
      title: "Late Check-ins Today",
      icon: null,
      color: "#F59E0B",
      group: "Time & Attendance",
      value: attendanceStats.late,
    },

    {
      id: "onLeaveToday",
      title: "On Leave Today",
      icon: null,
      color: "#3B82F6",
      group: "Leaves",
      value: leavesStats.onLeaveToday,
    },
    {
      id: "upcomingLeaves",
      title: "Upcoming Leaves (7d)",
      icon: null,
      color: "#8B5CF6",
      group: "Leaves",
      value: leavesStats.upcomingLeaves,
    },
    {
      id: "pendingLeaveRequests",
      title: "Pending Leave Requests",
      icon: null,
      color: "#F97316",
      group: "Leaves",
      value: leavesStats.pending,
    },
  ];

 

  const [visibleChartIds, setVisibleChartIds] = React.useState([]);
  const [visibleWidgetIds, setVisibleWidgetIds] = React.useState([]);
  const [chartViewModes, setChartViewModes] = React.useState({});

  React.useEffect(() => {
    const savedChartSettings = localStorage.getItem("chartDisplaySettings");
    const savedWidgetSettings = localStorage.getItem("widgetDisplaySettings");

    if (savedChartSettings) {
      const parsedSettings = JSON.parse(savedChartSettings) || [];
      const savedMap = new Map(
        parsedSettings.map((s) => [
          s.id,
          { isVisible: !!s.isVisible, viewMode: s.viewMode || "chart" },
        ])
      );

      const mergedVisibleChartIds = allChartConfigs
        .filter((config) => {
          const saved = savedMap.get(config.id);
          return saved ? saved.isVisible : true;
        })
        .map((c) => c.id);

      setVisibleChartIds(mergedVisibleChartIds);

      const modes = {};
      allChartConfigs.forEach((chart) => {
        const saved = savedMap.get(chart.id);
        modes[chart.id] = saved?.viewMode || "chart";
      });
      setChartViewModes(modes);
    } else {
      setVisibleChartIds(allChartConfigs.map((config) => config.id));
      const modes = {};
      allChartConfigs.forEach((chart) => (modes[chart.id] = "chart"));
      setChartViewModes(modes);
    }

    if (savedWidgetSettings) {
      const parsedSettings = JSON.parse(savedWidgetSettings) || [];
      const savedMap = new Map(
        parsedSettings.map((s) => [s.id, !!s.isVisible])
      );

      const mergedVisibleWidgetIds = allWidgetConfigs
        .filter((config) => {
          const savedVisible = savedMap.get(config.id);
          return savedVisible !== undefined ? savedVisible : true;
        })
        .map((w) => w.id);

      setVisibleWidgetIds(mergedVisibleWidgetIds);
    } else {
      setVisibleWidgetIds(allWidgetConfigs.map((config) => config.id));
    }
  }, []);

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };


  const handleWidgetClick = (widgetId, widgetTitle) => {
    if (widgetId === "totalworkforce") {
      navigate("/organization/employee/employee-details", { state: { fromDashboard: true, widgetId ,employmentType: "all" } });
      return;
    }
    if (widgetId === "totalEmployees" || widgetId === "newJoiningThisMonth") {
      navigate("/organization/employee/employee-details", { state: { fromDashboard: true, widgetId ,employmentType: "employee" } });
      return;
    }
    if (widgetId === "totalFreelancers" || widgetId === "newJoiningThisMonthFreelancers") {
      navigate("/organization/freelancer/freelancer-details", { state: { fromDashboard: true, widgetId ,employmentType: "freelancer" } });
      return;
    }
    if (widgetId === "totalInterns" || widgetId === "newJoiningThisMonthInterns") {
      navigate("/organization/intern/intern-details", { state: { fromDashboard: true, widgetId } });
      return;
    }
    if (widgetId === "birthdayThisMonth" || widgetId === "workAnniversariesThisMonth") {
      navigate("/organization/employee/employee-details", { state: { fromDashboard: true, widgetId } });
      return;
    }
    if (widgetId === "presentToday" || widgetId === "absentToday" || widgetId === "lateCheckInsToday") {
      navigate("/attendance/time-logs", { state: { fromDashboard: true, widgetId } });
      return;
    }
    if (widgetId === "onLeaveToday" || widgetId === "upcomingLeaves" || widgetId === "pendingLeaveRequests") {
      navigate("/leave/employee-leaves", { state: { fromDashboard: true, widgetId } });
      return;
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenPersonalizationPage = () => {
    navigate("dashboard/personalize-dashboard");
    handleCloseMenu();
  };

  const open = Boolean(anchorEl);

  // Lead summary + training program graph (EMS org dashboard)
  if (org) {
    const totalStudents = leadsSummary?.total_students ?? 0;
    const studentsAddedToday = leadsSummary?.students_added_today ?? 0;
    const feesDueThisMonth =
      feeInstallmentDueCounts?.monthPending ?? leadsSummary?.fees_due_this_month ?? 0;
    const feesDueToday =
      feeInstallmentDueCounts?.todayPending ?? leadsSummary?.fees_due_today ?? 0;
    const feesOverdue =
      feeInstallmentDueCounts?.overdue ?? leadsSummary?.fees_overdue ?? 0;

    const funnel = leadsSummary?.leads_funnel_metrics;

    // Backend uses `current`, dashboard UI expects `current_value`.
    const normalizeFunnelMetric = (m) => {
      if (!m) return null;
      if (typeof m !== "object") {
        return { current_value: m };
      }
      return {
        ...m,
        current_value: m.current_value ?? m.current ?? 0,
      };
    };

    const funnelNewLeads = normalizeFunnelMetric(funnel?.new_leads_this_month);
    const funnelQualified = normalizeFunnelMetric(
      funnel?.qualified_leads_this_month
    );
    const funnelDemo = normalizeFunnelMetric(funnel?.demo_leads_this_month);
    const funnelEnrolled = normalizeFunnelMetric(
      funnel?.enrolled_leads_this_month
    );

    const conversionCurrent =
      funnel?.current_month?.conversion_percent ??
      funnel?.current_month?.conversion ??
      0;
    const funnelConversion = normalizeFunnelMetric({
      label: "Conversion %",
      current_value: conversionCurrent,
      previous_month: funnel?.last_month?.conversion_percent ?? null,
      percent_change_vs_previous_month:
        funnel?.vs_last_month_percent_change?.conversion_percent ?? null,
      change_percentage_points_vs_previous_month:
        funnel?.vs_last_month_percent_change?.conversion_percentage_points ??
        null,
    });
    const currentStats = {
      trainingPrograms: leadsSummary?.active_training_programs_till_date ?? 0,
      trainers: leadsSummary?.active_trainers_till_date ?? 0,
      batches: leadsSummary?.active_batches_till_date ?? 0,
      students:
        leadsSummary?.batch_students_completed_till_date ??
        leadsSummary?.total_students ??
        0,
      openLeads:
        leadsSummary?.qualified_leads_till_date ??
        leadsSummary?.total_leads ??
        0,
    };

    const dashboardLeadMonth =
      funnel?.period?.current_month ||
      leadsSummary?.leads_funnel_metrics?.period?.current_month ||
      null;
    const monthQuery = dashboardLeadMonth
      ? `?month=${encodeURIComponent(dashboardLeadMonth)}`
      : "";

    const tpGraph = leadsSummary?.training_program_graph;
    const tpCategories = Array.isArray(tpGraph?.categories)
      ? tpGraph.categories
      : [];

    const summaryCardSx = {
      bgcolor:
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.05)"
          : "#ebebeb",
      backdropFilter: "blur(4px)",
      border: "none",
      boxShadow: 3,
      height: "160px",
      transition: "all 0.3s ease-in-out",
      "&:hover": {
        boxShadow: 6,
        transform: "scale(1.02)",
      },
      width: { xs: "80vw", sm: "50vw", lg: "16vw" },
      margin: "0 auto",
    };

    const funnelCardSx = {
      ...summaryCardSx,
      height: "auto",
      minHeight: 172,
      width: "100%",
      maxWidth: "100%",
    };

    const renderFunnelVsLastMonth = (metric) => {
      if (leadsSummaryLoading) {
        return (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.75, display: "block" }}
          >
            …
          </Typography>
        );
      }
      if (!metric) {
        return (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.75, display: "block" }}
          >
            — vs last month
          </Typography>
        );
      }
      const pct = Number(metric.percent_change_vs_previous_month);
      if (!Number.isFinite(pct)) {
        return (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.75, display: "block" }}
          >
            — vs last month
          </Typography>
        );
      }
      const color =
        pct === 0 ? "text.secondary" : pct > 0 ? "success.main" : "error.main";
      const abs = metric.change_vs_previous_month;
      const absPart =
        abs != null &&
        Number.isFinite(Number(abs)) &&
        Number(abs) !== 0
          ? ` (${Number(abs) >= 0 ? "+" : ""}${abs})`
          : "";
      return (
        <Typography
          variant="caption"
          sx={{ color, display: "block", mt: 0.75, fontWeight: 600 }}
        >
          {pct === 0
            ? "Flat vs last month"
            : pct > 0
              ? `Up ${Math.abs(pct).toFixed(1)}% vs last month`
              : `Down ${Math.abs(pct).toFixed(1)}% vs last month`}
          {absPart}
        </Typography>
      );
    };

    const renderConversionVsLastMonth = () => {
      const m = funnelConversion;
      if (leadsSummaryLoading) {
        return (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.75, display: "block" }}
          >
            …
          </Typography>
        );
      }
      if (!m) {
        return (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.75, display: "block" }}
          >
            — vs last month
          </Typography>
        );
      }
      const pp = Number(m.change_percentage_points_vs_previous_month);
      const rel = Number(m.percent_change_vs_previous_month);
      const ppColor =
        !Number.isFinite(pp) || pp === 0
          ? "text.secondary"
          : pp > 0
            ? "success.main"
            : "error.main";
      const relColor =
        !Number.isFinite(rel) || rel === 0
          ? "text.secondary"
          : rel > 0
            ? "success.main"
            : "error.main";
      return (
        <Box sx={{ mt: 0.75 }}>
          {/* {Number.isFinite(pp) && (
            // <Typography
            //   variant="caption"
            //   sx={{ color: ppColor, display: "block", fontWeight: 600 }}
            // >
            //   {pp >= 0 ? "+" : ""}
            //   {pp.toFixed(2)} pp vs last month
            // </Typography>
          )} */}
          {Number.isFinite(rel) && (
            <Typography variant="caption" sx={{ color: relColor, display: "block" }}>
              {rel >= 0 ? "+" : ""}
              {rel.toFixed(1)}% vs last month
            </Typography>
          )}
        </Box>
      );
    };

    const { bars: chartBars, maxVal: chartMax } = trainingChartData;
    const yAxisValues = (() => {
      if (!Number.isFinite(chartMax) || chartMax <= 0) return [0];

      // For small integer counts, show exact ticks (2,1,0) without duplicates.
      if (Number.isInteger(chartMax) && chartMax <= 8) {
        return Array.from({ length: chartMax + 1 }, (_, i) => chartMax - i);
      }

      // For larger values, keep axis compact with evenly spaced ticks.
      const steps = 5;
      return Array.from({ length: steps + 1 }, (_, i) => {
        const raw = chartMax - (chartMax / steps) * i;
        const value = chartMax <= 10 ? Math.round(raw * 10) / 10 : Math.round(raw);
        return i === steps ? 0 : value;
      });
    })();

    const handleTrainingGraphNavigate = (programName, categoryName) => {
      const params = new URLSearchParams();
      if (programName) params.set("training_program", programName);
      if (categoryName) params.set("training_program_category", categoryName);
      const query = params.toString();
      navigate(`/admissions${query ? `?${query}` : ""}`);
    };

    const EMS_DASHBOARD_SECTION_KEYS = [
      "Current_Statistics",
      "This_Month",
      "Students",
      "Fees",
      "Trainer_Details",
      "Graphs",
    ];
    const permList = Array.isArray(Permission) ? Permission : [];
    const usesEmsDashboardSectionPerms = EMS_DASHBOARD_SECTION_KEYS.some(
      (k) => permList.includes(k)
    );
    const showCurrentStatistics =
      !usesEmsDashboardSectionPerms || permList.includes("Current_Statistics");
    const showThisMonthLeads =
      !usesEmsDashboardSectionPerms || permList.includes("This_Month");
    const showStudentsSection =
      !usesEmsDashboardSectionPerms || permList.includes("Students");
    const showFeesSection =
      !usesEmsDashboardSectionPerms || permList.includes("Fees");
    const showTrainerDetailsSection =
      !usesEmsDashboardSectionPerms || permList.includes("Trainer_Details");
    const showGraphsSection =
      !usesEmsDashboardSectionPerms || permList.includes("Graphs");

    const initials = (name) => {
      const parts = String(name || "").trim().split(/\s+/);
      return parts.map((p) => p[0] || "").join("").toUpperCase().slice(0, 2);
    };

    const Sparkline = ({ points, dir, uid }) => {
      const w = 240, h = 46, pad = 3;
      if (!points || points.length < 2) return null;
      const max = Math.max(...points, 1);
      const min = Math.min(...points, 0);
      const span = max - min || 1;
      const stepX = (w - pad * 2) / (points.length - 1);
      const pts = points.map((v, i) => {
        const x = pad + i * stepX;
        const y = pad + (h - pad * 2) * (1 - (v - min) / span);
        return [x, y];
      });
      const line = pts.map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" ");
      const area = line + ` L${(w - pad).toFixed(1)} ${h} L${pad} ${h} Z`;
      const color = dir === "up" ? "var(--ems-green)" : dir === "down" ? "var(--ems-red)" : "var(--ems-faint)";
      const gid = "esg-" + uid;
      return (
        <svg className="ems-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.22" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill={`url(#${gid})`} />
          <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </svg>
      );
    };

    const mkSpark = (metric, isConversion = false) => {
      const current = Number(metric?.current_value ?? 0);
      const pct = isConversion
        ? Number(metric?.change_percentage_points_vs_previous_month ?? 0)
        : Number(metric?.percent_change_vs_previous_month ?? 0);
      const prev = (Number.isFinite(pct) && pct !== -100)
        ? current / (1 + pct / 100)
        : current;
      const p = Number.isFinite(prev) ? prev : current;
      return Array.from({ length: 7 }, (_, i) => p * (1 - i / 6) + current * (i / 6));
    };

    const renderTrend = (metric, isConversion = false) => {
      if (!metric) return null;
      const pct = isConversion
        ? Number(metric.change_percentage_points_vs_previous_month)
        : Number(metric.percent_change_vs_previous_month);
      if (!Number.isFinite(pct))
        return <span className="ems-trend-note">— vs last month</span>;
      if (pct === 0)
        return (
          <span className="ems-trend flat">
            <Minus size={11} />
            Flat
          </span>
        );
      const current = Number(metric.current_value ?? 0);
      let deltaStr = "";
      if (!isConversion && Number.isFinite(pct) && pct !== -100) {
        const prev = current / (1 + pct / 100);
        const delta = Math.round(current - prev);
        if (Number.isFinite(delta) && delta !== 0) {
          deltaStr = " · " + (delta > 0 ? "+" : "") + delta;
        }
      }
      return (
        <span className={`ems-trend ${pct > 0 ? "up" : "down"}`}>
          {pct > 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
          {Math.abs(pct).toFixed(1)}
          {isConversion ? " pp" : "%"}
          {deltaStr}
        </span>
      );
    };

    const chartDisplayData = trainingChartData.bars.map((b) => ({
      name: b.programName,
      total: b.total,
    }));

    return (
      <div className="ems-dashboard ems-fade-up">
        {/* Page head */}
        <div className="ems-page-head">
          <div>
            <h1 className="ems-page-title">Dashboard</h1>
            <div className="ems-page-sub">
              <span>{formattedDate}</span>
            </div>
          </div>
          <div className="ems-head-actions">
            <button
              className="ems-btn ems-ghost"
              onClick={() => window.location.reload()}
            >
              <RefreshCw size={15} />
              Refresh
            </button>
            <button
              className="ems-btn ems-primary"
              onClick={() => navigate("/leads/add")}
            >
              <Plus size={16} strokeWidth={2.5} />
              New Lead
            </button>
          </div>
        </div>

        {leadsSummaryError && (
          <div
            style={{ color: "var(--ems-red)", fontSize: 13, marginBottom: 12 }}
          >
            {leadsSummaryError}
          </div>
        )}

        {/* Current Statistics */}
        {showCurrentStatistics && (
          <section>
            <div className="ems-sec-head">
              <div className="ems-sec-title">
                <span className="ems-bar" />
                Current Statistics
              </div>
            </div>
            <div className="ems-grid ems-g-4">
              {[
                {
                  label: "Trainers",
                  value: currentStats.trainers,
                  icon: <UserCog size={20} />,
                },
                {
                  label: "Batches",
                  value: currentStats.batches,
                  icon: <Boxes size={20} />,
                  onClick: () => navigate("/batches?status=Ongoing"),
                },
                {
                  label: "Students",
                  value: currentStats.students,
                  icon: <GraduationCap size={20} />,
                  onClick: () =>
                    navigate("/batch-students?batch_status=Pursuing"),
                },
                {
                  label: "Open Leads",
                  value: currentStats.openLeads,
                  icon: <Target size={20} />,
                  onClick: () => navigate("/leads?lead_stage=Qualified"),
                },
              ].map((s) => (
                <div
                  key={s.label}
                  className="ems-card ems-stat"
                  style={s.onClick ? { cursor: "pointer" } : undefined}
                  onClick={s.onClick}
                  role={s.onClick ? "button" : undefined}
                  tabIndex={s.onClick ? 0 : undefined}
                  onKeyDown={
                    s.onClick
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") s.onClick();
                        }
                      : undefined
                  }
                >
                  <div className="ems-stat-top">
                    <div className="ems-stat-icon">{s.icon}</div>
                  </div>
                  <div className="ems-stat-label">{s.label}</div>
                  <div className="ems-stat-value ems-tnum">
                    {leadsSummaryLoading ? "…" : s.value}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Leads This Month */}
        {showThisMonthLeads && (
          <section>
            <div className="ems-sec-head">
              <div className="ems-sec-title">
                <span className="ems-bar" />
                Leads This Month
              </div>
              <button
                className="ems-sec-link"
                onClick={() => navigate(`/leads-monthly-funnel-report${monthQuery}`)}
              >
                View report <ArrowRight size={13} />
              </button>
            </div>
            <div className="ems-grid ems-g-5">
              {[
                { label: "New Leads", metric: funnelNewLeads },
                { label: "Qualified Leads", metric: funnelQualified },
                { label: "Demo", metric: funnelDemo },
                { label: "Enrolled", metric: funnelEnrolled },
                {
                  label: "Conversion %",
                  metric: funnelConversion,
                  isConversion: true,
                },
              ].map(({ label, metric, isConversion }) => {
                const rawVal = metric?.current_value ?? 0;
                const display = leadsSummaryLoading
                  ? "…"
                  : isConversion
                    ? Number.isFinite(Number(rawVal))
                      ? Number(rawVal).toFixed(2) + "%"
                      : "—"
                    : rawVal;
                const pct = isConversion
                  ? Number(metric?.change_percentage_points_vs_previous_month)
                  : Number(metric?.percent_change_vs_previous_month);
                const sparkDir = !Number.isFinite(pct) || pct === 0 ? "flat" : pct > 0 ? "up" : "down";
                return (
                  <div key={label} className="ems-card ems-stat" style={{ paddingBottom: 54 }}>
                    <div className="ems-stat-label" style={{ marginTop: 2 }}>
                      {label}
                    </div>
                    <div
                      className="ems-stat-value ems-tnum"
                      style={{ fontSize: isConversion ? 28 : 34 }}
                    >
                      {display}
                    </div>
                    <div className="ems-stat-foot">
                      {renderTrend(metric, isConversion)}
                      <span className="ems-trend-note">vs last month</span>
                    </div>
                    {!leadsSummaryLoading && (
                      <Sparkline points={mkSpark(metric, isConversion)} dir={sparkDir} uid={label.replace(/\s+/g, "-").toLowerCase()} />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Students + Fees */}
        {(showStudentsSection || showFeesSection) && (
          <div
            className="ems-grid"
            style={{
              gridTemplateColumns: "minmax(0,2fr) minmax(0,3fr)",
              alignItems: "start",
            }}
          >
            {showStudentsSection && (
              <section>
                <div className="ems-sec-head">
                  <div className="ems-sec-title">
                    <span className="ems-bar" />
                    Students
                  </div>
                </div>
                <div className="ems-grid ems-g-2">
                  {[
                    {
                      label: "Total Students",
                      value: totalStudents,
                      icon: <GraduationCap size={20} />,
                      onClick: () => navigate("/batch-students"),
                    },
                    {
                      label: "New Students",
                      value: studentsAddedToday,
                      icon: <UserPlus size={20} />,
                    },
                  ].map((s) => (
                    <div
                      key={s.label}
                      className="ems-card ems-stat"
                      style={s.onClick ? { cursor: "pointer" } : undefined}
                      onClick={s.onClick}
                      role={s.onClick ? "button" : undefined}
                      tabIndex={s.onClick ? 0 : undefined}
                    >
                      <div className="ems-stat-top">
                        <div className="ems-stat-icon">{s.icon}</div>
                      </div>
                      <div className="ems-stat-label">{s.label}</div>
                      <div className="ems-stat-value ems-tnum">
                        {leadsSummaryLoading ? "…" : s.value}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
            {showFeesSection && (
              <section>
                <div className="ems-sec-head">
                  <div className="ems-sec-title">
                    <span className="ems-bar" />
                    Fees
                  </div>
                </div>
                <div className="ems-grid ems-g-3">
                  {[
                    {
                      label: `Fees Due (${currentMonthName})`,
                      value: feesDueThisMonth,
                      icon: <CalendarClock size={20} />,
                      tone: "warn",
                      note: "Upcoming this month",
                      onClick: () =>
                        navigate("/fee-installments?status=Pending&due=month"),
                    },
                    {
                      label: "Fees Due (Today)",
                      value: feesDueToday,
                      icon: <Wallet size={20} />,
                      tone: "ok",
                      note: "Nothing due today",
                      onClick: () =>
                        navigate("/fee-installments?status=Pending&due=today"),
                    },
                    {
                      label: "Fees Overdue",
                      value: feesOverdue,
                      icon: <AlertCircle size={20} />,
                      tone: "bad",
                      note: "Needs follow-up",
                      onClick: () =>
                        navigate("/fee-installments?status=Overdue"),
                    },
                  ].map((s) => (
                    <div
                      key={s.label}
                      className="ems-card ems-stat"
                      style={s.onClick ? { cursor: "pointer" } : undefined}
                      onClick={s.onClick}
                      role={s.onClick ? "button" : undefined}
                      tabIndex={s.onClick ? 0 : undefined}
                    >
                      <div className="ems-stat-top">
                        <div className={`ems-stat-icon ${s.tone}`}>
                          {s.icon}
                        </div>
                      </div>
                      <div className="ems-stat-label">{s.label}</div>
                      <div className="ems-stat-value ems-tnum">
                        {leadsSummaryLoading ? "…" : s.value ?? "—"}
                      </div>
                      <div className="ems-stat-foot">
                        <span className="ems-trend-note">{s.note}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {/* Batch Trainer Details */}
        {showTrainerDetailsSection && (
          <section style={{ marginTop: 30 }}>
            <div className="ems-card ems-table-card">
              <div className="ems-table-toolbar">
                <Boxes
                  size={18}
                  style={{ color: "var(--ems-primary)", flexShrink: 0 }}
                />
                <span className="ems-tt-title">Batch trainer details</span>
                {!hasLoggedInTrainerAssignments &&
                  trainerOptions.length > 0 && (
                    <select
                      className="ems-select"
                      value={effectiveSelectedBatchTrainerId}
                      onChange={(e) =>
                        setSelectedBatchTrainerId(e.target.value)
                      }
                    >
                      <option value="">All trainers</option>
                      {trainerOptions.map((opt) => (
                        <option
                          key={opt.trainer_employee_id}
                          value={String(opt.trainer_employee_id)}
                        >
                          {batchTrainerLabel(opt)}
                        </option>
                      ))}
                    </select>
                  )}
                <button
                  className="ems-btn ems-sm"
                  onClick={exportBatchTrainerDetailsToExcel}
                  style={{ marginLeft: "auto" }}
                >
                  <Download size={14} />
                  Export
                </button>
              </div>
              <div className="ems-table-wrap">
                <table className="ems-data">
                  <thead>
                    <tr>
                      <th>Batch</th>
                      <th>Training Program</th>
                      <th>Trainer</th>
                      <th className="ems-num">Students</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Batch Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTrainerAssignments.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          style={{
                            textAlign: "center",
                            color: "var(--ems-faint)",
                            padding: "30px 18px",
                          }}
                        >
                          No batches found.
                        </td>
                      </tr>
                    ) : (
                      selectedTrainerAssignments.map((row, idx) => {
                        const tname = batchTrainerLabel(row);
                        const days = row?.batch?.batch_days;
                        const daysArr = Array.isArray(days) ? days : [];
                        return (
                          <tr key={idx}>
                            <td>
                              <span className="ems-batch-tag">
                                {row?.batch?.batch_code ||
                                  row?.batch_code ||
                                  "—"}
                              </span>
                            </td>
                            <td className="ems-prog-cell">
                              {row?.batch?.training_program
                                ?.training_program_name || "—"}
                            </td>
                            <td>
                              <div className="ems-trainer-cell">
                                <div className="ems-t-avatar">
                                  {initials(tname)}
                                </div>
                                {tname || "—"}
                              </div>
                            </td>
                            <td className="ems-num">
                              <span className="ems-stud-badge ems-tnum">
                                {batchStudentCountForAssignment(row)}
                              </span>
                            </td>
                            <td>
                              <span className="ems-time-pill">
                                {formatTimeAmPm(classStartTimeForRow(row))}
                              </span>
                            </td>
                            <td>
                              <span className="ems-time-pill">
                                {formatTimeAmPm(classEndTimeForRow(row))}
                              </span>
                            </td>
                            <td>
                              {daysArr.length > 0 ? (
                                <div className="ems-days-chips">
                                  {daysArr.map((d) => (
                                    <span key={d} className="ems-day-chip">
                                      {d}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="ems-trend-note">
                                  {formatBatchDays(days) || "—"}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {/* Training Program Graph */}
        {showGraphsSection && (
          <section style={{ marginTop: 24 }}>
            <div className="ems-card ems-chart-card">
              <div className="ems-chart-head">
                <div>
                  <div className="ems-sec-title" style={{ fontSize: 17 }}>
                    <span className="ems-bar" />
                    Training Program Graph
                  </div>
                  <p className="ems-chart-desc">
                    Students by program (admissions) —{" "}
                    <strong>
                      {chartDisplayData.reduce((a, b) => a + b.total, 0)}
                    </strong>{" "}
                    total
                    {trainingGraphCategory !== "all" &&
                      tpCategories[trainingGraphCategory] && (
                        <>
                          {" "}
                          ·{" "}
                          <strong>
                            {tpCategories[trainingGraphCategory]}
                          </strong>
                        </>
                      )}
                  </p>
                </div>
                {tpCategories.length > 0 && (
                  <select
                    className="ems-select"
                    value={
                      trainingGraphCategory === "all"
                        ? "all"
                        : trainingGraphCategory
                    }
                    onChange={(e) =>
                      setTrainingGraphCategory(
                        e.target.value === "all"
                          ? "all"
                          : Number(e.target.value)
                      )
                    }
                  >
                    <option value="all">All categories</option>
                    {tpCategories.map((cat, i) => (
                      <option key={i} value={i}>
                        {cat}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {chartDisplayData.length === 0 ? (
                <div className="ems-empty">
                  <div className="ems-empty-icon">
                    <BookOpen size={26} />
                  </div>
                  <div style={{ fontWeight: 650 }}>No program data yet</div>
                  <div style={{ fontSize: 12.5, marginTop: 4 }}>
                    Admissions data will appear here once students are enrolled.
                  </div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={chartDisplayData}
                    margin={{ top: 12, right: 16, left: -10, bottom: 120 }}
                    onClick={(d) => {
                      if (d?.activePayload?.[0]?.payload?.name) {
                        handleTrainingGraphNavigate(
                          d.activePayload[0].payload.name,
                          trainingGraphCategory !== "all"
                            ? tpCategories[trainingGraphCategory]
                            : undefined
                        );
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <XAxis
                      dataKey="name"
                      interval={0}
                      tick={({ x, y, payload }) => {
                        const label = payload.value || "";
                        const maxChars = 22;
                        const display = label.length > maxChars ? label.slice(0, maxChars - 1) + "…" : label;
                        return (
                          <g transform={`translate(${x},${y})`}>
                            <text
                              x={0}
                              y={0}
                              dy={8}
                              transform="rotate(-40)"
                              textAnchor="end"
                              fill="var(--ems-muted)"
                              fontSize={11}
                              fontFamily="Manrope, sans-serif"
                            >
                              {display}
                            </text>
                          </g>
                        );
                      }}
                    />
                    <YAxis
                      tick={{
                        fontSize: 12,
                        fill: "var(--ems-faint)",
                        fontFamily: "Manrope, sans-serif",
                      }}
                      allowDecimals={false}
                    />
                    <RechartsTooltip
                      cursor={{ fill: "var(--ems-primary-tint-2)" }}
                      contentStyle={{
                        background: "var(--ems-surface)",
                        border: "1px solid var(--ems-border)",
                        borderRadius: 10,
                        fontSize: 13,
                        fontFamily: "Manrope, sans-serif",
                        color: "var(--ems-text)",
                      }}
                      formatter={(value) => [value, "Students"]}
                    />
                    <Bar dataKey="total" radius={[6, 6, 0, 0]} maxBarSize={54}>
                      {chartDisplayData.map((_, i) => (
                        <Cell
                          key={i}
                          fill={`var(--ems-chart-${(i % 6) + 1})`}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </section>
        )}
      </div>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? theme.palette.background.default
            : "#ffffff",
        display: "flex",
      }}
    >
      <Box
        sx={{
          width: drawerOpen ? "280px" : "0px",
          height: "100vh",
          backgroundColor: theme.palette.background.paper,
          boxShadow:
            theme.palette.mode === "dark"
              ? "2px 0 10px rgba(255,255,255,0.1)"
              : "2px 0 10px rgba(0,0,0,0.1)",
          transition: "width 0.3s ease-in-out",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "sticky",
          top: 0,
        }}
      >
        <Box sx={{ flex: 1, p: 2, minWidth: "280px" }}>
          {[
            {
              text: "Dashboard",
              icon: <BarChartIcon />,
            },
            {
              text: "Employees",
              icon: <PersonIcon />,
            },
            {
              text: "Reports",
              icon: <ShowChart />,
            },
            {
              text: "Calendar",
              icon: <CalendarToday />,
            },
          ].map((item) => (
            <Box
              key={item.text}
              sx={{
                display: "flex",
                alignItems: "center",
                p: 2,
                mb: 1,
                borderRadius: 2,
                cursor: "pointer",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.08)"
                      : "rgba(0, 0, 0, 0.04)",
                },
              }}
              onClick={() => handleMenuItemClick(item.text)}
            >
              <Box sx={{ mr: 2, color: "text.secondary" }}>{item.icon}</Box>
              <Typography variant="body1" sx={{ color: "text.primary" }}>
                {item.text}
              </Typography>
            </Box>
          ))}
          <Box
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              mt: 2,
              pt: 2,
            }}
          >
            {["Settings", "Help", "Logout"].map((item) => (
              <Box
                key={item}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 2,
                  mb: 1,
                  borderRadius: 2,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.08)"
                        : "rgba(0, 0, 0, 0.04)",
                  },
                }}
                onClick={() => handleMenuItemClick(item)}
              >
                <Box sx={{ mr: 2, color: "text.secondary" }}>
                  {item === "Settings" ? <SettingsIcon /> : <MoveToInbox />}
                </Box>
                <Typography variant="body1" sx={{ color: "text.primary" }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          p: 3,
          overflow: "auto",
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box>
              <Typography
                variant="h5"
                component="h2"
                sx={{ fontWeight: "semibold", color: "text.primary", mb: 1 }}
              >
                Dashboard
              </Typography>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                {formattedDate}
              </Typography>
            </Box>
          </Box>

          {Permission && Permission.includes("PERSONALIZE_DASHBOARD") && (
            <>
              <Button
                aria-controls={open ? "settings-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={handleClick}
                sx={{ minWidth: "auto", p: 1, borderRadius: "50%" }}
              >
                <SettingsIcon sx={{ fontSize: 28, color: "text.secondary" }} />
              </Button>

              <Menu
                id="settings-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleCloseMenu}
                MenuListProps={{
                  "aria-labelledby": "settings-button",
                }}
              >
                <MenuItem onClick={handleOpenPersonalizationPage}>
                  <DashboardCustomize sx={{ mr: 1 }} />
                  Personalize Dashboard
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>

        <Grid container spacing={3}>
            <hr style={{ border: 'none', borderTop: '3px solid #000' , width:"100%"}} />
          {(allWidgetConfigs.some(
            (w) => w.group === "Employees" && visibleWidgetIds.includes(w.id)
            
          ) ||
            allWidgetConfigs.some(
              (w) => w.group === "Occasions" && visibleWidgetIds.includes(w.id)
            ) ||
            allWidgetConfigs.some(
              (w) => w.group === "Total Workforce" && visibleWidgetIds.includes(w.id)
            ) ||
            allWidgetConfigs.some(
              (w) => w.group === "Interns" && visibleWidgetIds.includes(w.id)
            ) ||
            allWidgetConfigs.some(
              (w) =>
                w.group === "Freelancers" && visibleWidgetIds.includes(w.id)
            ) ||
            allWidgetConfigs.some(
              (w) =>
                w.group === "Time & Attendance" &&
                visibleWidgetIds.includes(w.id)
            ) ||
            allWidgetConfigs.some(
              (w) => w.group === "Leaves" && visibleWidgetIds.includes(w.id)
            )) && (
            <Grid container spacing={2}>
              
              {/* Employees */}
              {allWidgetConfigs
                .filter((w) => w.group === "Total Workforce")
                .some((w) => visibleWidgetIds.includes(w.id)) && (
                <Grid item xs={12} md={6}>
                  
                  <Typography
                  
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: "semibold",
                      color: "text.primary",
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    Total Workforce
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {allWidgetConfigs
                      .filter(
                        (w) =>
                          w.group === "Total Workforce" &&
                          visibleWidgetIds.includes(w.id)
                      )
                      .map((widget) => (
                        <Grid item xs={12} sm={12} md={6} key={widget.id}>
                          <Card
                            sx={{
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "#ebebeb",
                              backdropFilter: "blur(4px)",
                              border: "none",
                              boxShadow: 3,
                              transition: "all 0.3s ease-in-out",
                              "&:hover": {
                                boxShadow: 6,
                                transform: "scale(1.02)",
                              },
                              height: "160px",
                              width: { xs: "80vw", sm: "50vw", lg: "17vw" },
                              margin: "0 auto",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              navigate(widget.link)
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleWidgetClick(widget.id, widget.title);
                              }
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              
                              {widget.id === "totalworkforce" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title={widget.title} placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      {widget.title}
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    Total
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : widget.id === "totalEmployees" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title="Total Employess" placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      Employees
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    Total
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : widget.id === "totalFreelancers" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title="Total Employess" placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      Freelancers
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    Total
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        color: "text.secondary",
                                        fontWeight: "medium",
                                        mb: 2,
                                      }}
                                    >
                                      {widget.title}
                                    </Typography>
                                    <Typography
                                      variant="h4"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                      }}
                                    >
                                      {widget.value}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                  </Grid>
                </Grid>
              )}
              {/* Employees */}
              {allWidgetConfigs
                .filter((w) => w.group === "Employees")
                .some((w) => visibleWidgetIds.includes(w.id)) && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: "semibold",
                      color: "text.primary",
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    Employees
                  </Typography>
                  <Grid container spacing={2}>
                    {allWidgetConfigs
                      .filter(
                        (w) =>
                          w.group === "Employees" &&
                          visibleWidgetIds.includes(w.id)
                      )
                      .map((widget) => (
                        <Grid item xs={12} sm={12} md={6} key={widget.id}>
                          <Card
                            sx={{
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "#ebebeb",
                              backdropFilter: "blur(4px)",
                              border: "none",
                              boxShadow: 3,
                              transition: "all 0.3s ease-in-out",
                              "&:hover": {
                                boxShadow: 6,
                                transform: "scale(1.02)",
                              },
                              height: "160px",
                              width: { xs: "80vw", sm: "50vw", lg: "17vw" },
                              margin: "0 auto",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              navigate(widget.link)
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleWidgetClick(widget.id, widget.title);
                              }
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              {widget.id === "totalEmployees" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title={widget.title} placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      {widget.title}
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    Total
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : widget.id === "newJoiningThisMonth" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title="New Joining" placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      New Joining
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    This Month
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        color: "text.secondary",
                                        fontWeight: "medium",
                                        mb: 2,
                                      }}
                                    >
                                      {widget.title}
                                    </Typography>
                                    <Typography
                                      variant="h4"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                      }}
                                    >
                                      {widget.value}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                  </Grid>
                </Grid>
              )}

              {/* Occasions */}
              {allWidgetConfigs
                .filter((w) => w.group === "Occasions")
                .some((w) => visibleWidgetIds.includes(w.id)) && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: "semibold",
                      color: "text.primary",
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    Occasions
                  </Typography>
                  <Grid container spacing={2}>
                    {allWidgetConfigs
                      .filter(
                        (w) =>
                          w.group === "Occasions" &&
                          visibleWidgetIds.includes(w.id)
                      )
                      .map((widget) => (
                        <Grid item xs={12} sm={12} key={widget.id}>
                          <Card
                            sx={{
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "#ebebeb",
                              backdropFilter: "blur(4px)",
                              border: "none",
                              boxShadow: 3,
                              transition: "all 0.3s ease-in-out",
                              "&:hover": {
                                boxShadow: 6,
                                transform: "scale(1.02)",
                              },
                              height: "160px",
                              width: { xs: "80vw", sm: "50vw", lg: "17vw" },
                              margin: "0 auto",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              navigate(widget.link)
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleWidgetClick(widget.id, widget.title);
                              }
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              {widget.id === "birthdayThisMonth" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title="Birthday" placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      Birthday
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    This Month
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : widget.id === "workAnniversariesThisMonth" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip
                                    title="Work Anniversaries"
                                    placement="top"
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      Work Anniversaries
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    This Month
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        color: "text.secondary",
                                        fontWeight: "medium",
                                        mb: 2,
                                      }}
                                    >
                                      {widget.title}
                                    </Typography>
                                    <Typography
                                      variant="h4"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                      }}
                                    >
                                      {widget.value}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                  </Grid>
                </Grid>
              )}

              {/* Interns - UPDATED with proper functionality */}
              {allWidgetConfigs
                .filter((w) => w.group === "Interns")
                .some((w) => visibleWidgetIds.includes(w.id)) && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: "semibold",
                      color: "text.primary",
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    Interns
                  </Typography>
                  <Grid container spacing={2}>
                    {allWidgetConfigs
                      .filter(
                        (w) =>
                          w.group === "Interns" &&
                          visibleWidgetIds.includes(w.id)
                      )
                      .map((widget) => (
                        <Grid item xs={12} sm={12} md={6} key={widget.id}>
                          <Card
                            sx={{
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "#ebebeb",
                              backdropFilter: "blur(4px)",
                              border: "none",
                              boxShadow: 3,
                              transition: "all 0.3s ease-in-out",
                              "&:hover": {
                                boxShadow: 6,
                                transform: "scale(1.02)",
                              },
                              height: "160px",
                              width: { xs: "80vw", sm: "50vw", lg: "17vw" },
                              margin: "0 auto",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              handleWidgetClick(widget.id, widget.title)
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleWidgetClick(widget.id, widget.title);
                              }
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              {widget.id === "totalInterns" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title={widget.title} placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      {widget.title}
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    Total
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : widget.id === "newJoiningThisMonthInterns" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title="New Joining" placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      New Joining
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    This Month
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        color: "text.secondary",
                                        fontWeight: "medium",
                                        mb: 2,
                                      }}
                                    >
                                      {widget.title}
                                    </Typography>
                                    <Typography
                                      variant="h4"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                      }}
                                    >
                                      {widget.value}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                  </Grid>
                </Grid>
              )}


              {/* Freelancers */}
              {allWidgetConfigs
                .filter((w) => w.group === "Freelancers")
                .some((w) => visibleWidgetIds.includes(w.id)) && (
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: "semibold",
                      color: "text.primary",
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    Freelancers
                  </Typography>
                  <Grid container spacing={2}>
                    {allWidgetConfigs
                      .filter(
                        (w) =>
                          w.group === "Freelancers" &&
                          visibleWidgetIds.includes(w.id)
                      )
                      .map((widget) => (
                        <Grid item xs={12} sm={12} md={6} key={widget.id}>
                          <Card
                            sx={{
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "#ebebeb",
                              backdropFilter: "blur(4px)",
                              border: "none",
                              boxShadow: 3,
                              transition: "all 0.3s ease-in-out",
                              "&:hover": {
                                boxShadow: 6,
                                transform: "scale(1.02)",
                              },
                              height: "160px",
                              width: { xs: "80vw", sm: "50vw", lg: "17vw" },
                              margin: "0 auto",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              navigate(widget.link)
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ")
                                handleWidgetClick(widget.id, widget.title);
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              {widget.id === "totalFreelancers" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title={widget.title} placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      {widget.title}
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    Total
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : widget.id ===
                                "newJoiningThisMonthFreelancers" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    height: "100%",
                                  }}
                                >
                                  <Tooltip title="New Joining" placement="top">
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                        mb: 0.5,
                                        display: "block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      New Joining
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "medium",
                                      mb: 2,
                                    }}
                                  >
                                    This Month
                                  </Typography>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      fontWeight: "semibold",
                                      color: "text.primary",
                                    }}
                                  >
                                    {widget.value}
                                  </Typography>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        color: "text.secondary",
                                        fontWeight: "medium",
                                        mb: 2,
                                      }}
                                    >
                                      {widget.title}
                                    </Typography>
                                    <Typography
                                      variant="h4"
                                      sx={{
                                        fontWeight: "semibold",
                                        color: "text.primary",
                                      }}
                                    >
                                      {widget.value}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                  </Grid>
                </Grid>
              )}

              {/* Time & Attendance and Leaves */}
              {(allWidgetConfigs
                .filter((w) => w.group === "Time & Attendance")
                .some((w) => visibleWidgetIds.includes(w.id)) ||
                allWidgetConfigs
                  .filter((w) => w.group === "Leaves")
                  .some((w) => visibleWidgetIds.includes(w.id))) && (
                <Grid container spacing={2} sx={{ mt: 0 }}>
                  {allWidgetConfigs
                    .filter((w) => w.group === "Time & Attendance")
                    .some((w) => visibleWidgetIds.includes(w.id)) && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="h6"
                        component="h2"
                        sx={{
                          fontWeight: "semibold",
                          color: "text.primary",
                          mb: 3,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        Time & Attendance
                      </Typography>
                      <Grid container spacing={2}>
                        {allWidgetConfigs
                          .filter(
                            (w) =>
                              w.group === "Time & Attendance" &&
                              visibleWidgetIds.includes(w.id)
                          )
                          .map((widget) => (
                            <Grid item xs={12} sm={12} md={6} key={widget.id}>
                              <Card
                                sx={{
                                  bgcolor:
                                    theme.palette.mode === "dark"
                                      ? "rgba(255, 255, 255, 0.05)"
                                      : "#ebebeb",
                                  backdropFilter: "blur(4px)",
                                  border: "none",
                                  boxShadow: 3,
                                  transition: "all 0.3s ease-in-out",
                                  "&:hover": {
                                    boxShadow: 6,
                                    transform: "scale(1.02)",
                                  },
                                  height: "160px",
                                  width: { xs: "80vw", sm: "50vw", lg: "17vw" },
                                  margin: "0 auto",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  handleWidgetClick(widget.id, widget.title)
                                }
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    handleWidgetClick(widget.id, widget.title);
                                  }
                                }}
                              >
                                <CardContent sx={{ p: 3 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <Box>
                                      <Typography
                                        variant="subtitle1"
                                        sx={{
                                          color: "text.secondary",
                                          fontWeight: "medium",
                                          mb: 2,
                                        }}
                                      >
                                        {widget.title}
                                      </Typography>
                                      <Typography
                                        variant="h4"
                                        sx={{
                                          fontWeight: "semibold",
                                          color: "text.primary",
                                        }}
                                      >
                                        {widget.value}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                      </Grid>
                    </Grid>
                  )}

                  {allWidgetConfigs
                    .filter((w) => w.group === "Leaves")
                    .some((w) => visibleWidgetIds.includes(w.id)) && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="h6"
                        component="h2"
                        sx={{
                          fontWeight: "semibold",
                          color: "text.primary",
                          mb: 3,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        Leaves
                      </Typography>
                      <Grid container spacing={2}>
                        {allWidgetConfigs
                          .filter(
                            (w) =>
                              w.group === "Leaves" &&
                              visibleWidgetIds.includes(w.id)
                          )
                          .map((widget) => (
                            <Grid item xs={12} sm={12} md={6} key={widget.id}>
                              <Card
                                sx={{
                                  bgcolor:
                                    theme.palette.mode === "dark"
                                      ? "rgba(255, 255, 255, 0.05)"
                                      : "#ebebeb",
                                  backdropFilter: "blur(4px)",
                                  border: "none",
                                  boxShadow: 3,
                                  transition: "all 0.3s ease-in-out",
                                  "&:hover": {
                                    boxShadow: 6,
                                    transform: "scale(1.02)",
                                  },
                                  height: "160px",
                                  width: { xs: "80vw", sm: "50vw", lg: "17vw" },
                                  margin: "0 auto",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  handleWidgetClick(widget.id, widget.title)
                                }
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    handleWidgetClick(widget.id, widget.title);
                                  }
                                }}
                              >
                                <CardContent sx={{ p: 3 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <Box>
                                      <Typography
                                        variant="subtitle1"
                                        sx={{
                                          color: "text.secondary",
                                          fontWeight: "medium",
                                          mb: 2,
                                        }}
                                      >
                                        {widget.title}
                                      </Typography>
                                      <Typography
                                        variant="h4"
                                        sx={{
                                          fontWeight: "semibold",
                                          color: "text.primary",
                                        }}
                                      >
                                        {widget.value}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>
          )}

          {allChartConfigs.some((chart) =>
            visibleChartIds.includes(chart.id)
          ) && (
            <Box sx={{ mt: 6 }}>
                <hr style={{ border: 'none', borderTop: '3px solid #000' , width:"100%"}} />
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  fontWeight: "semibold",
                  color: "text.primary",
                  mb: 4,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <ShowChart sx={{ fontSize: 28, color: "#4F46E5" }} />
                Employee Distribution Analytics
              </Typography>
              
              <Grid container spacing={2}>
                {allChartConfigs
                  .filter((chart) => visibleChartIds.includes(chart.id))
                  .map((chart) => (
                    <Grid item lg={6} sm={12} key={chart.id}>
                      <Card
                        sx={{
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.05)"
                              : "#ebebeb",
                          backdropFilter:
                            chartViewModes[chart.id] === "chart"
                              ? "blur(4px)"
                              : "none",
                          border: "none",
                          boxShadow: 3,
                          width: {
                            xs: "80vw",
                            sm: "80vw",
                            md: "80vw",
                            lg: "43vw",
                          },
                          height: "450px",
                          display: "flex",
                          flexDirection: "column",
                          "&:hover": {
                            boxShadow: 6,
                            transform:
                              chartViewModes[chart.id] === "chart"
                                ? "scale(1.02)"
                                : "none",
                          },
                        }}
                      >
                        <CardContent
                          sx={{
                            p: 3,
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: "semibold",
                              color: "text.primary",
                              mb: 2,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {chart.icon &&
                              React.createElement(chart.icon, {
                                sx: { fontSize: 20, color: chart.color },
                              })}
                            {chart.title}
                          </Typography>

                          {chartViewModes[chart.id] === "chart" ? (
                            <Box
                              sx={{
                                flex: 1,
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: 240,
                                borderRadius: 1,
                                border: "1px dashed",
                                borderColor: "divider",
                                bgcolor: "background.paper",
                                p: 2,
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                align="center"
                              >
                                Charts are unavailable. Install{" "}
                                <code>@mui/x-charts</code> to enable this section.
                              </Typography>
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                flex: 1,
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <DataGrid
                                rowBuffer={2}
                                rowThreshold={0}
                                sx={{
                                  flex: 1,
                                  "& .MuiDataGrid-main": {
                                    height: "100%",
                                  },
                                }}
                                rows={chart.data.map((row, index) => ({
                                  id: `${chart.id}-${index}`,
                                  label: row.label || row.name,
                                  value: row.value,
                                }))}
                                columns={[
                                  {
                                    field: "label",
                                    headerName:
                                      chart.type === "pie" ? "Label" : "Name",
                                    flex: 1,
                                    headerAlign: "center",
                                    align: "center",
                                  },
                                  {
                                    field: "value",
                                    headerName: "Value",
                                    type: "number",
                                    flex: 1,
                                    headerAlign: "center",
                                    align: "center",
                                  },
                                ]}
                                pageSizeOptions={[5, 10, 25]}
                                initialState={{
                                  pagination: {
                                    paginationModel: { pageSize: 5 },
                                  },
                                }}
                                disableRowSelectionOnClick
                              />
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            </Box>
          )}
        </Grid>
      </Box>
    </Box>
  );
}
